/**
 * /dashboard — User dashboard with full monitoring
 *
 * Tabs:
 *   Overview  — plan status, conversion usage, MCP list
 *   Analytics — per-MCP call volume, top tools, daily chart
 *   Monitoring — error log feed, uptime %, p95 latency, error categories
 */

import { useEffect, useRef, useState, useCallback, useMemo } from "react";
import { Link, useSearchParams, useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import {
  Crown, Zap, BarChart3, Clock, CheckCircle, XCircle, ChevronDown,
  ExternalLink, RefreshCw, AlertTriangle, Sparkles, Copy,
  Activity, Shield, AlertCircle, TrendingUp, Radio,
  CheckCircle2, Wifi, WifiOff, Timer, GitBranch, Upload,
  Trash2, Play, ChevronRight, FileText, Webhook as WebhookIcon, Plus,
  Key, Lock, List, RotateCcw, MapPin, Smartphone, Bell, Link2, Share2, Gift,
} from "lucide-react";
import TopHeader from "../components/TopHeader";
import Footer from "../components/Footer";
import {
  getDashboardData, getMcpUsage, getErrorLogs,
  getUptimeStats, getProbeUptimeStats, syncPlanFromStripe,
  getAlertPreferences, updateAlertPreferences, testSlackWebhook, testDiscordWebhook,
  getReferralStats, claimReferral, type ReferralStats,
  createShareToken, listShareTokens, revokeShareToken, type ShareToken,
  listMcpVersions, uploadMcpVersion, activateMcpVersion, deleteMcpVersion,
  listWebhooks, createWebhook, deleteWebhook, pingWebhook, listWebhookDeliveries,
  rotateWebhookSecret,
  WEBHOOK_EVENTS,
  listApiKeys, createApiKey, revokeApiKey, rotateApiKey,
  getAuditLogs, listIpAllowlist, addIpAllowlistEntry, deleteIpAllowlistEntry,
  get2faStatus, setup2fa, verify2fa, disable2fa,
  getActivityFeed, getMcpHealthHistory,
  type HostedMcp, type McpUsage, type SubscriptionStatus,
  type ErrorLogEntry, type UptimeStats, type UserSummary, type McpVersion,
  type Webhook, type WebhookDelivery,
  type ApiKey, type AuditEvent, type IpAllowlistEntry, type TotpStatus, type TotpSetup,
  type AlertPreferences, type ProbeUptimeStats, type CallLogEntry, type HealthCheckRow,
} from "../lib/subscriptionApi";
import { resolveHostedEndpoint } from "../api";
import { readDashboardCache, writeDashboardCache } from "../lib/dashboardCache";
import { LIVE_STATS_REFRESH_MS } from "../lib/liveRefresh";

// ─────────────────────────────────────────────────────────────────────────────
// Helpers
// ─────────────────────────────────────────────────────────────────────────────

function formatDate(iso: string | null): string {
  if (!iso) return "—";
  return new Date(iso).toLocaleDateString(undefined, {
    month: "short", day: "numeric", year: "numeric",
  });
}

function formatRelative(iso: string): string {
  const diff = Date.now() - new Date(iso).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return "just now";
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.floor(hrs / 24);
  return `${days}d ago`;
}

function daysUntil(iso: string | null): number | null {
  if (!iso) return null;
  return Math.ceil((new Date(iso).getTime() - Date.now()) / 86400000);
}

function copyText(text: string) {
  navigator.clipboard.writeText(text).catch(() => {});
}

// ─────────────────────────────────────────────────────────────────────────────
// Stat card
// ─────────────────────────────────────────────────────────────────────────────

function StatCard({
  label, value, sub, icon, accent = "blue", pulse = false, pending = false,
}: {
  label: string;
  value: string | number;
  sub?: string;
  icon: React.ReactNode;
  accent?: "blue" | "green" | "amber" | "red" | "violet";
  pulse?: boolean;
  pending?: boolean;
}) {
  const colors: Record<string, string> = {
    blue:   "bg-blue-500/10 text-blue-500",
    green:  "bg-emerald-500/10 text-emerald-500",
    amber:  "bg-amber-500/10 text-amber-500",
    red:    "bg-red-500/10 text-red-500",
    violet: "bg-violet-500/10 text-violet-500",
  };
  return (
    <div className="rounded-2xl border border-border bg-card p-5">
      <div className="flex items-center justify-between mb-3">
        <div className={`w-9 h-9 rounded-xl flex items-center justify-center ${colors[accent]}`}>
          {icon}
        </div>
        {pulse && (
          <span className="flex items-center gap-1.5 text-xs text-emerald-500 font-medium">
            <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse" />
            Live
          </span>
        )}
      </div>
      <p className={`text-2xl font-bold tabular-nums ${pending ? "text-muted-foreground/40 animate-pulse" : "text-foreground"}`}>
        {pending ? "—" : value}
      </p>
      <p className="text-xs text-muted-foreground mt-0.5">{label}</p>
      {sub && <p className="text-xs text-muted-foreground/70 mt-0.5">{sub}</p>}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Mini bar chart (stacked calls + errors)
// ─────────────────────────────────────────────────────────────────────────────

function StackedBarChart({ daily }: { daily: McpUsage["daily"] }) {
  if (!daily.length) {
    return (
      <div className="flex items-center justify-center h-16 text-xs text-muted-foreground italic">
        No data yet
      </div>
    );
  }
  const max = Math.max(...daily.map(d => d.calls), 1);
  return (
    <div className="flex items-end gap-px h-16" title="Blue = success, Red = errors">
      {daily.map(d => {
        const totalH = (d.calls / max) * 100;
        const errH = d.calls > 0 ? (d.errors / d.calls) * totalH : 0;
        const okH = totalH - errH;
        return (
          <div
            key={d.day}
            className="flex-1 flex flex-col justify-end min-w-[3px]"
            title={`${d.day}\n${d.calls} calls, ${d.errors} errors`}
          >
            {errH > 0 && (
              <div className="w-full rounded-t-sm bg-red-400/80" style={{ height: `${errH}%` }} />
            )}
            <div
              className="w-full bg-blue-500/70 hover:bg-blue-500 transition-colors"
              style={{ height: `${okH}%`, borderRadius: errH > 0 ? "0" : "2px 2px 0 0" }}
            />
          </div>
        );
      })}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Error log row
// ─────────────────────────────────────────────────────────────────────────────

function ErrorRow({ entry, index }: { entry: ErrorLogEntry; index: number }) {
  const [expanded, setExpanded] = useState(false);
  const isWarning = entry.severity === "warning";

  return (
    <motion.div
      initial={{ opacity: 0, x: -8 }}
      animate={{ opacity: 1, x: 0 }}
      transition={{ delay: index * 0.03 }}
      className={`rounded-xl border overflow-hidden transition-colors ${
        isWarning
          ? "border-amber-200 dark:border-amber-800/60 bg-amber-50/50 dark:bg-amber-950/20"
          : "border-red-200 dark:border-red-800/60 bg-red-50/50 dark:bg-red-950/20"
      }`}
    >
      <button
        onClick={() => setExpanded(e => !e)}
        className="w-full flex items-center gap-3 px-4 py-3 text-left hover:bg-black/[0.02] dark:hover:bg-white/[0.02] transition-colors"
      >
        {/* Severity icon */}
        <div className="shrink-0">
          {isWarning ? (
            <AlertTriangle size={14} className="text-amber-500" />
          ) : (
            <XCircle size={14} className="text-red-500" />
          )}
        </div>

        {/* Tool badge */}
        <code className="shrink-0 text-xs font-mono px-2 py-0.5 rounded-md bg-black/5 dark:bg-white/5 text-foreground">
          {entry.tool}
        </code>

        {/* Message preview */}
        <span className="flex-1 text-xs text-muted-foreground truncate">
          {entry.message}
        </span>

        {/* Latency */}
        {entry.response_ms != null && (
          <span className="shrink-0 text-xs text-muted-foreground tabular-nums">
            {entry.response_ms}ms
          </span>
        )}

        {/* Time */}
        <span className="shrink-0 text-xs text-muted-foreground/60">
          {formatRelative(entry.called_at)}
        </span>

        <ChevronDown
          size={13}
          className={`shrink-0 text-muted-foreground transition-transform ${expanded ? "rotate-180" : ""}`}
        />
      </button>

      {/* Expanded detail */}
      <AnimatePresence initial={false}>
        {expanded && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.18 }}
            className="overflow-hidden"
          >
            <div className="px-4 pb-3 pt-1 border-t border-inherit">
              <div className="flex items-start gap-2">
                <pre className="flex-1 text-xs font-mono text-foreground/80 whitespace-pre-wrap break-all bg-black/5 dark:bg-white/5 rounded-lg p-3 leading-relaxed">
                  {entry.message}
                </pre>
              </div>
              <div className="flex items-center gap-4 mt-2 text-[11px] text-muted-foreground">
                <span>Tool: <strong className="text-foreground">{entry.tool}</strong></span>
                {entry.response_ms != null && (
                  <span>Duration: <strong className="text-foreground">{entry.response_ms}ms</strong></span>
                )}
                <span>Time: <strong className="text-foreground">{new Date(entry.called_at).toLocaleString()}</strong></span>
                <span className={`ml-auto font-semibold ${isWarning ? "text-amber-600" : "text-red-600"}`}>
                  {isWarning ? "⚠ WARNING" : "✕ ERROR"}
                </span>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Uptime donut (SVG)
// ─────────────────────────────────────────────────────────────────────────────

function UptimeDonut({ pct }: { pct: number }) {
  const r = 28, cx = 36, cy = 36;
  const circ = 2 * Math.PI * r;
  const filled = (pct / 100) * circ;
  const color = pct >= 99.9 ? "#10b981" : pct >= 99 ? "#3b82f6" : pct >= 95 ? "#f59e0b" : "#ef4444";

  return (
    <svg width="72" height="72" viewBox="0 0 72 72">
      <circle cx={cx} cy={cy} r={r} fill="none" stroke="currentColor" strokeWidth="7"
        className="text-muted/40" />
      <circle cx={cx} cy={cy} r={r} fill="none" stroke={color} strokeWidth="7"
        strokeDasharray={`${filled} ${circ}`}
        strokeLinecap="round"
        transform={`rotate(-90 ${cx} ${cy})`}
        style={{ transition: "stroke-dasharray 0.6s ease" }}
      />
      <text x={cx} y={cy + 1} textAnchor="middle" dominantBaseline="middle"
        fontSize="10" fontWeight="700" fill={color}>
        {pct.toFixed(1)}%
      </text>
    </svg>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// MCP selector dropdown
// ─────────────────────────────────────────────────────────────────────────────

function McpSelector({
  mcps, selected, onChange,
}: {
  mcps: HostedMcp[];
  selected: string | null;
  onChange: (id: string) => void;
}) {
  const [open, setOpen] = useState(false);
  const current = mcps.find(m => m.server_id === selected);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const h = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener("mousedown", h);
    return () => document.removeEventListener("mousedown", h);
  }, [open]);

  if (!mcps.length) return null;

  return (
    <div ref={ref} className="relative">
      <button
        onClick={() => setOpen(o => !o)}
        className="flex items-center gap-2 px-3 py-2 rounded-xl border border-border bg-card hover:bg-accent text-sm transition-colors"
      >
        <Activity size={14} className="text-blue-500" />
        <span className="font-medium text-foreground truncate max-w-[180px]">
          {current?.title ?? "Select MCP"}
        </span>
        <ChevronDown size={13} className={`text-muted-foreground transition-transform ${open ? "rotate-180" : ""}`} />
      </button>
      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, y: -4, scale: 0.97 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: -4, scale: 0.97 }}
            transition={{ duration: 0.13 }}
            className="absolute right-0 top-full mt-1.5 z-50 w-64 rounded-xl border border-border bg-card shadow-xl overflow-hidden"
          >
            {mcps.map(m => (
              <button
                key={m.server_id}
                onClick={() => { onChange(m.server_id); setOpen(false); }}
                className={`w-full flex items-center gap-2.5 px-3 py-2.5 text-sm text-left hover:bg-accent transition-colors ${
                  m.server_id === selected ? "bg-blue-500/10 text-blue-600 dark:text-blue-400" : "text-foreground"
                }`}
              >
                <div className={`w-1.5 h-1.5 rounded-full shrink-0 ${m.is_expired ? "bg-zinc-400" : "bg-emerald-500"}`} />
                <span className="truncate flex-1">{m.title}</span>
                <span className="text-xs text-muted-foreground shrink-0">{m.tool_count} tools</span>
              </button>
            ))}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// MCP health badge (proactive cron probes)
// ─────────────────────────────────────────────────────────────────────────────

type HealthStatus = "healthy" | "degraded" | "down" | null | undefined;

function classifyHealthError(raw: string | null | undefined): { message: string; hint: string | null } {
  if (!raw) return { message: "Unknown error", hint: null };
  const s = raw.toLowerCase();
  // Auth failures
  if (s.includes("401") || s.includes("403"))
    return { message: "Authentication failed", hint: "Your API key may be expired or invalid — update it in Credentials." };
  // Gateway / rate limit
  if (s.includes("429"))
    return { message: "Rate limit hit", hint: "Your upstream API is throttling requests. Try again later." };
  // Upstream server errors
  if (s.includes("502") || s.includes("503") || s.includes("504"))
    return { message: "Upstream API unavailable", hint: "The API your MCP calls appears to be down. No action needed — we'll keep probing." };
  if (s.includes("http 5") || s.includes("http5"))
    return { message: "Upstream API error", hint: "The API returned a server error. This may be temporary." };
  // MCP expired
  if (s.includes("410") || s.includes("mcp expired"))
    return { message: "MCP expired", hint: "This MCP has expired. Upgrade to Pro to keep it alive." };
  // Timeout
  if (s.includes("timeout") || s.includes("timed out"))
    return { message: "Server not responding", hint: "The MCP server is timing out. It may be starting up (cold start) or overloaded." };
  // Cold start
  if (s.includes("cold start"))
    return { message: "Cold start detected", hint: "The server woke up slowly. This is normal after inactivity — subsequent calls will be fast." };
  // SSE / streaming issues
  if (s.includes("sse"))
    return { message: "Connection stream failed", hint: "The event stream could not be established. This is usually transient." };
  // tools/list
  if (s.includes("tools/list"))
    return { message: "Tool discovery failed", hint: "The MCP server responded but couldn't list tools. The spec may have changed." };
  // Fallback — show raw but truncated
  return { message: raw.length > 80 ? raw.slice(0, 80) + "…" : raw, hint: null };
}

function StatusBadge({ status }: { status: HealthStatus }) {
  if (!status) {
    return (
      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-muted text-muted-foreground text-[10px] font-medium">
        <Radio size={10} /> Unknown
      </span>
    );
  }

  const styles: Record<string, string> = {
    healthy: "bg-emerald-50 dark:bg-emerald-950/50 text-emerald-700 dark:text-emerald-400",
    degraded: "bg-amber-100 dark:bg-amber-950 text-amber-700 dark:text-amber-400",
    down: "bg-red-100 dark:bg-red-950 text-red-700 dark:text-red-400",
  };
  const icons: Record<string, string> = {
    healthy: "✅",
    degraded: "⚠️",
    down: "🔴",
  };
  const label = status.charAt(0).toUpperCase() + status.slice(1);

  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-medium ${styles[status] ?? "bg-muted"}`}>
      <span aria-hidden>{icons[status]}</span>
      {label}
    </span>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// MCP card (overview tab)
// ─────────────────────────────────────────────────────────────────────────────

function Sparkline({ data }: { data: number[] }) {
  if (!data.length) return null;
  const max = Math.max(...data, 1);
  const w = 56, h = 20, pad = 1;
  const pts = data.map((v, i) => {
    const x = pad + (i / Math.max(data.length - 1, 1)) * (w - pad * 2);
    const y = h - pad - ((v / max) * (h - pad * 2));
    return `${x},${y}`;
  }).join(" ");
  const fill = data[data.length - 1] > data[0] ? "#22c55e" : "#3b82f6";
  return (
    <svg width={w} height={h} className="shrink-0">
      <polyline points={pts} fill="none" stroke={fill} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" opacity="0.8" />
      <circle cx={pts.split(" ").at(-1)!.split(",")[0]} cy={pts.split(" ").at(-1)!.split(",")[1]} r="2" fill={fill} />
    </svg>
  );
}

function McpCard({
  mcp,
  onViewAnalytics,
  showUsageAnalytics,
  email,
}: {
  mcp: HostedMcp;
  onViewAnalytics: () => void;
  showUsageAnalytics: boolean;
  email: string | null;
}) {
  const [copied, setCopied] = useState(false);
  const [sparkData, setSparkData] = useState<number[] | null>(null);
  const [shareOpen, setShareOpen] = useState(false);
  const [shareUrl, setShareUrl] = useState<string | null>(null);
  const [shareLoading, setShareLoading] = useState(false);
  const [shareCopied, setShareCopied] = useState(false);

  useEffect(() => {
    if (!showUsageAnalytics || mcp.is_expired) return;
    import("../lib/subscriptionApi").then(({ getMcpUsage }) =>
      getMcpUsage(mcp.server_id, 7)
        .then(u => setSparkData(u.daily.map(d => d.calls)))
        .catch(() => {})
    );
  }, [mcp.server_id, showUsageAnalytics, mcp.is_expired]);
  const daysLeft = daysUntil(mcp.expires_at);
  const isExpired = mcp.is_expired;
  const isExpiringSoon = !isExpired && daysLeft !== null && daysLeft <= 2;
  const mcpPathSegment = (mcp.public_path?.trim() || mcp.server_id).trim();
  const mcpUrl = resolveHostedEndpoint(`/mcp/${mcpPathSegment}`);

  const handleCopy = () => {
    copyText(mcpUrl);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      className={`rounded-2xl border bg-card p-5 transition-colors ${
        isExpired ? "border-border opacity-55" : isExpiringSoon ? "border-amber-400/50" : "border-border"
      }`}
    >
      <div className="flex items-start gap-3">
        <div className={`w-9 h-9 rounded-xl flex items-center justify-center shrink-0 mt-0.5 ${
          isExpired ? "bg-muted" : "bg-blue-500/10"
        }`}>
          <Activity size={16} className={isExpired ? "text-muted-foreground" : "text-blue-500"} />
        </div>

        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="font-semibold text-foreground text-sm truncate">{mcp.title}</span>
            {isExpired ? (
              <span className="flex items-center gap-1 px-2 py-0.5 rounded-full bg-muted text-muted-foreground text-[10px] font-medium">
                <XCircle size={10} /> Expired
              </span>
            ) : mcp.expires_at && daysLeft !== null ? (
              <span
                className={`flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-medium ${
                  isExpiringSoon
                    ? "bg-amber-100 dark:bg-amber-950 text-amber-700 dark:text-amber-400"
                    : "bg-emerald-50 dark:bg-emerald-950/50 text-emerald-700 dark:text-emerald-400"
                }`}
                title={`Expires ${formatDate(mcp.expires_at)}`}
              >
                {isExpiringSoon ? <AlertTriangle size={10} /> : <CheckCircle size={10} />}
                {daysLeft <= 0 ? "Expires today" : `${daysLeft}d left`}
              </span>
            ) : (
              <span className="flex items-center gap-1 px-2 py-0.5 rounded-full bg-blue-50 dark:bg-blue-950/50 text-blue-600 dark:text-blue-400 text-[10px] font-medium">
                <Sparkles size={10} /> Never expires
              </span>
            )}
            {!isExpired && <StatusBadge status={mcp.health_status} />}
            {mcp.source_url && (
              <span className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded-md text-[9px] font-medium bg-blue-50 dark:bg-blue-950/40 text-blue-600 dark:text-blue-400 border border-blue-200 dark:border-blue-800">
                <RefreshCw size={8} /> Auto-updating
              </span>
            )}
          </div>
          <div className="flex items-center justify-between gap-3 mt-1">
            <div className="flex items-center gap-3 text-[11px] text-muted-foreground">
              <span><Zap size={10} className="inline mr-0.5" />{mcp.tool_count} tools</span>
              <span><Clock size={10} className="inline mr-0.5" />Created {formatDate(mcp.created_at)}</span>
              {showUsageAnalytics && !mcp.is_expired && (mcp.calls_today ?? 0) > 0 && (
                <span className="font-medium text-foreground">
                  <Activity size={10} className="inline mr-0.5 text-blue-500" />
                  {mcp.calls_today!.toLocaleString()} calls today
                </span>
              )}
            </div>
            {showUsageAnalytics && !mcp.is_expired && sparkData && sparkData.some(v => v > 0) && (
              <Sparkline data={sparkData} />
            )}
          </div>

          {!isExpired && (
            <div className="mt-1.5 text-[11px] text-muted-foreground">
              {mcp.last_health_check ? (
                <span>
                  Last checked: {formatRelative(mcp.last_health_check)}
                  {mcp.health_response_ms != null && mcp.health_status === "healthy" && (
                    <span className="text-emerald-600 dark:text-emerald-400"> · {mcp.health_response_ms}ms</span>
                  )}
                </span>
              ) : (
                <span className="italic">Health check pending…</span>
              )}
              {(mcp.health_status === "down" || mcp.health_status === "degraded") && mcp.last_health_error && (() => {
                const { message, hint } = classifyHealthError(mcp.last_health_error);
                const isDown = mcp.health_status === "down";
                return (
                  <div className={`mt-1 flex flex-col gap-0.5 ${isDown ? "text-red-600 dark:text-red-400" : "text-amber-700 dark:text-amber-400"}`}>
                    <div className="flex items-start gap-1">
                      <AlertTriangle size={11} className="shrink-0 mt-0.5" />
                      <span className="font-medium">{message}</span>
                    </div>
                    {hint && <p className="text-[10px] text-muted-foreground pl-4 leading-snug">{hint}</p>}
                  </div>
                );
              })()}
            </div>
          )}

          {!isExpired && (
            <div className="mt-2.5 flex items-center gap-1.5">
              <code className="flex-1 text-[10px] bg-muted/60 rounded-lg px-2.5 py-1.5 truncate font-mono text-muted-foreground">
                {mcpUrl}
              </code>
              <button onClick={handleCopy} className="p-1.5 rounded-lg border border-border hover:bg-muted transition-colors text-muted-foreground hover:text-foreground shrink-0">
                {copied ? <CheckCircle2 size={12} className="text-emerald-500" /> : <Copy size={12} />}
              </button>
              <a href={mcpUrl} target="_blank" rel="noopener noreferrer" className="p-1.5 rounded-lg border border-border hover:bg-muted transition-colors text-muted-foreground hover:text-foreground shrink-0">
                <ExternalLink size={12} />
              </a>
              {showUsageAnalytics && (
              <button
                onClick={onViewAnalytics}
                className="p-1.5 rounded-lg border border-border hover:bg-muted transition-colors text-muted-foreground hover:text-foreground shrink-0"
                title="View analytics"
              >
                <BarChart3 size={12} />
              </button>
              )}
              <button
                onClick={async () => {
                  if (shareOpen) { setShareOpen(false); return; }
                  setShareOpen(true);
                  if (shareUrl || !email) return;
                  setShareLoading(true);
                  try {
                    const t = await createShareToken(mcp.server_id, email);
                    setShareUrl(`${window.location.origin}/share/${t.token}`);
                  } catch { /* ignore */ } finally {
                    setShareLoading(false);
                  }
                }}
                className={`p-1.5 rounded-lg border transition-colors shrink-0 ${shareOpen ? "border-blue-400 bg-blue-50 dark:bg-blue-950/40 text-blue-500" : "border-border hover:bg-muted text-muted-foreground hover:text-foreground"}`}
                title="Share MCP stats"
              >
                <Share2 size={12} />
              </button>
            </div>
          )}

          {/* Share panel */}
          {shareOpen && !mcp.is_expired && (
            <div className="mt-2 p-3 rounded-xl bg-blue-50 dark:bg-blue-950/30 border border-blue-200 dark:border-blue-800/50 space-y-2">
              <p className="text-xs font-semibold text-blue-700 dark:text-blue-300">Share read-only stats link</p>
              <p className="text-[10px] text-blue-600/80 dark:text-blue-400/80">Anyone with this link can view this MCP's call stats — no login needed.</p>
              {shareLoading ? (
                <p className="text-xs text-blue-500">Generating link…</p>
              ) : shareUrl ? (
                <div className="flex items-center gap-2">
                  <code className="flex-1 text-[10px] bg-white dark:bg-zinc-900 rounded-lg px-2.5 py-1.5 font-mono text-foreground truncate border border-blue-200 dark:border-blue-800">
                    {shareUrl}
                  </code>
                  <button
                    onClick={() => { navigator.clipboard.writeText(shareUrl); setShareCopied(true); setTimeout(() => setShareCopied(false), 2000); }}
                    className="shrink-0 p-1.5 rounded-lg bg-blue-600 hover:bg-blue-700 text-white transition-colors"
                  >
                    {shareCopied ? <CheckCircle2 size={12} /> : <Copy size={12} />}
                  </button>
                </div>
              ) : (
                <p className="text-xs text-red-500">Failed to generate link</p>
              )}
            </div>
          )}
        </div>
      </div>
    </motion.div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Main page
// ─────────────────────────────────────────────────────────────────────────────

type Tab = "overview" | "analytics" | "monitoring" | "activity" | "versions" | "webhooks" | "security" | "integrations";

export default function DashboardPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [email, setEmail] = useState<string | null>(null);
  const [status, setStatus] = useState<SubscriptionStatus | null>(null);
  const [usageSummary, setUsageSummary] = useState<UserSummary | null>(null);
  const [mcps, setMcps] = useState<HostedMcp[] | null>(null);
  const [sessionChecked, setSessionChecked] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [toast, setToast] = useState<string | null>(null);
  const [tab, setTab] = useState<Tab>("overview");

  // Per-MCP analytics state
  const [selectedServer, setSelectedServer] = useState<string | null>(null);
  const [usage, setUsage] = useState<McpUsage | null>(null);
  const [loadingUsage, setLoadingUsage] = useState(false);

  // Monitoring state
  const [errorLogs, setErrorLogs] = useState<ErrorLogEntry[] | null>(null);
  const [uptimeStats, setUptimeStats] = useState<UptimeStats | null>(null);
  const [probeStats, setProbeStats] = useState<ProbeUptimeStats | null>(null);
  const [healthHistory, setHealthHistory] = useState<HealthCheckRow[] | null>(null);
  const [loadingMonitor, setLoadingMonitor] = useState(false);
  const [monitorDays, setMonitorDays] = useState(7);
  const [autoRefresh, setAutoRefresh] = useState(false);
  const autoRefreshRef = useRef<ReturnType<typeof setInterval> | null>(null);

  // Alert preferences state
  const [alertPrefs, setAlertPrefs] = useState<AlertPreferences | null>(null);
  const [alertPrefsSaving, setAlertPrefsSaving] = useState(false);
  const [alertPrefsSaved, setAlertPrefsSaved] = useState(false);
  const [slackTesting, setSlackTesting] = useState(false);
  const [slackTestResult, setSlackTestResult] = useState<"ok" | "error" | null>(null);
  const [discordTesting, setDiscordTesting] = useState(false);
  const [discordTestResult, setDiscordTestResult] = useState<"ok" | "error" | null>(null);
  const [referralStats, setReferralStats] = useState<ReferralStats | null>(null);
  const [referralCopied, setReferralCopied] = useState(false);
  const [shareTokens, setShareTokens] = useState<ShareToken[]>([]);
  const [shareTokensLoading, setShareTokensLoading] = useState<Record<string, boolean>>({});
  const [copiedShareToken, setCopiedShareToken] = useState<string | null>(null);
  const dashboardFetchInFlight = useRef(false);

  // Activity tab state
  const [activityItems, setActivityItems] = useState<CallLogEntry[]>([]);
  const [activityTotal, setActivityTotal] = useState(0);
  const [activityPage, setActivityPage] = useState(0);
  const [activityLoading, setActivityLoading] = useState(false);
  const [activityError, setActivityError] = useState<string | null>(null);
  const [activityFilter, setActivityFilter] = useState<"all" | "success" | "error">("all");

  // Versions tab state
  const [versions, setVersions] = useState<McpVersion[] | null>(null);
  const [versionsLoading, setVersionsLoading] = useState(false);
  const [versionsError, setVersionsError] = useState<string | null>(null);
  const [versionUploadText, setVersionUploadText] = useState("");
  const [versionUploadNotes, setVersionUploadNotes] = useState("");
  const [versionUploading, setVersionUploading] = useState(false);
  const [versionActivating, setVersionActivating] = useState<string | null>(null);
  const [versionDeleting, setVersionDeleting] = useState<string | null>(null);
  const [showUploadForm, setShowUploadForm] = useState(false);

  // Webhooks tab state
  const [webhooks, setWebhooks] = useState<Webhook[] | null>(null);
  const [webhooksLoading, setWebhooksLoading] = useState(false);
  const [webhooksError, setWebhooksError] = useState<string | null>(null);
  const [showWebhookForm, setShowWebhookForm] = useState(false);
  const [whUrl, setWhUrl] = useState("");
  const [whEvents, setWhEvents] = useState<string[]>(["mcp.call.completed", "mcp.error.occurred"]);
  const [whServerId, setWhServerId] = useState<string>("__all__");
  const [whCreating, setWhCreating] = useState(false);
  const [whDeleting, setWhDeleting] = useState<string | null>(null);
  const [whPinging, setWhPinging] = useState<string | null>(null);
  const [whDeliveries, setWhDeliveries] = useState<Record<string, WebhookDelivery[]>>({});
  const [whShowDeliveries, setWhShowDeliveries] = useState<string | null>(null);
  // One-time reveal — populated when the user creates a webhook or rotates the
  // secret. Cleared once the user acknowledges. List-row data never contains
  // the plaintext secret, so this is the only place a customer can copy it.
  const [whRevealedSecret, setWhRevealedSecret] = useState<{ id: string; secret: string } | null>(null);
  const [whRotating, setWhRotating] = useState<string | null>(null);

  // Security tab state
  const [secApiKeys, setSecApiKeys] = useState<ApiKey[] | null>(null);
  const [secApiKeysLoading, setSecApiKeysLoading] = useState(false);
  const [secNewKeyName, setSecNewKeyName] = useState("Default");
  const [secCreatingKey, setSecCreatingKey] = useState(false);
  const [secRevealedKey, setSecRevealedKey] = useState<string | null>(null);

  const [secAuditLogs, setSecAuditLogs] = useState<AuditEvent[] | null>(null);
  const [secAuditLoading, setSecAuditLoading] = useState(false);
  const [secAuditDays, setSecAuditDays] = useState(30);

  const [secIpList, setSecIpList] = useState<IpAllowlistEntry[] | null>(null);
  const [secIpLoading, setSecIpLoading] = useState(false);
  const [secNewCidr, setSecNewCidr] = useState("");
  const [secNewCidrLabel, setSecNewCidrLabel] = useState("");
  const [secAddingIp, setSecAddingIp] = useState(false);

  const [sec2fa, setSec2fa] = useState<TotpStatus | null>(null);
  const [sec2faSetup, setSec2faSetup] = useState<TotpSetup | null>(null);
  const [sec2faCode, setSec2faCode] = useState("");
  const [sec2faLoading, setSec2faLoading] = useState(false);
  const [sec2faSection, setSec2faSection] = useState<"idle" | "setup" | "disable">("idle");

  // Integrations tab state
  const [integrations, setIntegrations] = useState<import("../lib/registryApi").RegistryConnection[] | null>(null);
  const [integrationsLoading, setIntegrationsLoading] = useState(false);
  const [integrationsError, setIntegrationsError] = useState<string | null>(null);
  const [revokingConnection, setRevokingConnection] = useState<string | null>(null);

  const navigate = useNavigate();
  const checkoutSuccess = searchParams.get("checkout") === "success";

  const applyDashboardData = useCallback((data: Awaited<ReturnType<typeof getDashboardData>>) => {
    const { mcps: m, ...rest } = data;
    setStatus({
      email: rest.email,
      plan: rest.plan,
      conversions_used_this_month: rest.conversions_used_this_month,
      conversions_left: rest.conversions_left,
      monthly_limit: rest.monthly_limit,
      current_period_end: rest.current_period_end,
      stripe_customer_id: rest.stripe_customer_id,
    });
    setMcps(m);
    setUsageSummary({
      plan: rest.plan,
      conversions_used_this_month: rest.conversions_used_this_month,
      monthly_limit: rest.monthly_limit,
      active_mcps: rest.active_mcps,
      calls_today: rest.calls_today,
      total_calls: rest.total_calls,
    });
    const firstActive = m.find(x => !x.is_expired);
    if (firstActive) setSelectedServer(firstActive.server_id);
  }, []);

  // Session + cache first so the page never spins forever on a slow API.
  useEffect(() => {
    let cancelled = false;
    fetch("/api/auth/session")
      .then(r => r.ok ? r.json() : null)
      .then((s: { user?: { email?: string } } | null) => {
        if (cancelled) return;
        const addr = s?.user?.email?.trim().toLowerCase() || null;
        setEmail(addr);
        if (addr) {
          const cached = readDashboardCache(addr);
          if (cached) applyDashboardData(cached);
        }
      })
      .catch(() => { if (!cancelled) setEmail(null); })
      .finally(() => { if (!cancelled) setSessionChecked(true); });
    return () => { cancelled = true; };
  }, [applyDashboardData]);

  const reloadMcps = useCallback((opts?: { silent?: boolean }) => {
    if (!email || dashboardFetchInFlight.current) return;
    dashboardFetchInFlight.current = true;
    const silent = opts?.silent ?? false;
    if (!silent) setRefreshing(true);
    getDashboardData(email)
      .then((data) => {
        applyDashboardData(data);
        writeDashboardCache(email, data);
        window.dispatchEvent(new Event("dashboard-data-updated"));
      })
      .catch((err: unknown) => {
        if (!silent && !readDashboardCache(email)) {
          setToast(err instanceof Error ? err.message : "Could not load dashboard data");
        }
      })
      .finally(() => {
        dashboardFetchInFlight.current = false;
        if (!silent) setRefreshing(false);
      });
  }, [email, applyDashboardData]);

  useEffect(() => {
    if (!sessionChecked || !email) return;
    reloadMcps();
  }, [sessionChecked, email, reloadMcps]);

  useEffect(() => {
    const onMcpChanged = () => reloadMcps();
    window.addEventListener("user-mcp-changed", onMcpChanged);
    return () => window.removeEventListener("user-mcp-changed", onMcpChanged);
  }, [reloadMcps]);

  // Load usage when selected server or analytics tab changes
  useEffect(() => {
    if (tab !== "analytics" || !selectedServer) return;
    setLoadingUsage(true);
    getMcpUsage(selectedServer, 30)
      .then(setUsage)
      .catch(() => {})
      .finally(() => setLoadingUsage(false));
  }, [tab, selectedServer]);

  // Load monitoring data
  const loadMonitoring = useCallback(() => {
    if (!selectedServer) return;
    setLoadingMonitor(true);
    Promise.all([
      getErrorLogs(selectedServer, 50, monitorDays),
      getUptimeStats(selectedServer, 30),
      getProbeUptimeStats(selectedServer, 30),
      getMcpHealthHistory(selectedServer, 48),
    ])
      .then(([logs, uptime, probe, history]) => {
        setErrorLogs(logs);
        setUptimeStats(uptime);
        setProbeStats(probe);
        setHealthHistory(history);
      })
      .catch(() => {})
      .finally(() => setLoadingMonitor(false));
  }, [selectedServer, monitorDays]);

  useEffect(() => {
    if (tab !== "monitoring") return;
    loadMonitoring();
    // Load alert preferences once when monitoring tab first opens
    if (!alertPrefs && email) {
      getAlertPreferences(email)
        .then(setAlertPrefs)
        .catch(() => {});
    }
  }, [tab, loadMonitoring]);

  // Load activity feed when tab opens or filters change
  useEffect(() => {
    if (tab !== "activity") return;
    setActivityLoading(true);
    setActivityError(null);
    getActivityFeed({
      serverId: selectedServer ?? undefined,
      limit: 50,
      page: activityPage,
      status: activityFilter === "all" ? undefined : activityFilter,
    })
      .then(data => {
        if (activityPage === 0) {
          setActivityItems(data.items);
        } else {
          setActivityItems(prev => [...prev, ...data.items]);
        }
        setActivityTotal(data.total);
      })
      .catch(() => setActivityError("Failed to load activity."))
      .finally(() => setActivityLoading(false));
  }, [tab, selectedServer, activityPage, activityFilter]);

  // Auto-refresh monitoring
  useEffect(() => {
    if (autoRefreshRef.current) clearInterval(autoRefreshRef.current);
    if (autoRefresh && tab === "monitoring") {
      autoRefreshRef.current = setInterval(loadMonitoring, 30000);
    }
    return () => {
      if (autoRefreshRef.current) clearInterval(autoRefreshRef.current);
    };
  }, [autoRefresh, tab, loadMonitoring]);

  // Live stats: poll every 5s while dashboard is open (no manual refresh).
  useEffect(() => {
    if (!sessionChecked || !email) return;

    const refreshLive = () => {
      if (document.visibilityState !== "visible") return;
      reloadMcps({ silent: true });
      if (tab === "analytics" && selectedServer) {
        getMcpUsage(selectedServer, 30).then(setUsage).catch(() => {});
      }
      if (tab === "monitoring" && selectedServer) {
        loadMonitoring();
      }
    };

    const intervalId = window.setInterval(refreshLive, LIVE_STATS_REFRESH_MS);
    document.addEventListener("visibilitychange", refreshLive);
    window.addEventListener("focus", refreshLive);

    return () => {
      window.clearInterval(intervalId);
      document.removeEventListener("visibilitychange", refreshLive);
      window.removeEventListener("focus", refreshLive);
    };
  }, [sessionChecked, email, reloadMcps, tab, selectedServer, loadMonitoring]);

  // After Paddle payment — sync plan; only celebrate when actually Pro
  useEffect(() => {
    if (!checkoutSuccess || !email) return;

    const next = new URLSearchParams(searchParams);
    next.delete("checkout");
    setSearchParams(next, { replace: true });

    void syncPlanFromStripe(email).then(async ({ plan }) => {
      const { getDashboardData } = await import("../lib/subscriptionApi");
      try {
        const data = await getDashboardData(email);
        applyDashboardData(data);
        writeDashboardCache(email, data);
      } catch { /* ignore */ }

      if (plan === "pro") {
        setToast("🎉 Welcome to Pro! Your subscription is now active.");
        const { trackUserUpgraded } = await import("../analytics");
        trackUserUpgraded("pro");
      } else {
        setToast(
          "Payment received — Pro may take a minute to activate. Refresh if it doesn't update.",
        );
      }
    });
  }, [checkoutSuccess, email, searchParams, setSearchParams, applyDashboardData]);

  useEffect(() => {
    if (!toast) return;
    const t = setTimeout(() => setToast(null), 5000);
    return () => clearTimeout(t);
  }, [toast]);

  // Load referral stats once when we have an email
  useEffect(() => {
    if (!email || referralStats) return;
    getReferralStats(email).then(setReferralStats).catch(() => {});
  }, [email]);

  const handleUpgrade = () => {
    if (!email) return;
    navigate("/pricing");
  };

  const switchToAnalytics = useCallback((serverId: string) => {
    setSelectedServer(serverId);
    setTab("analytics");
  }, []);

  const isPro = status?.plan === "pro";
  const conversionsUsed = status?.conversions_used_this_month ?? 0;
  const monthlyLimit = status?.monthly_limit ?? 3; // null for Pro — only used when !isPro
  const conversionsLeft = status?.conversions_left ?? null;
  const showUpgradeNudge = !isPro && conversionsLeft !== null && conversionsLeft > 0 && conversionsLeft <= 2;
  const statsReady = usageSummary !== null;
  const activeMcps = mcps?.filter(m => !m.is_expired) ?? [];
  const expiredMcps = mcps?.filter(m => m.is_expired) ?? [];
  const allMcps = mcps ?? [];
  const errorCount = errorLogs?.length ?? 0;
  const warningCount = errorLogs?.filter(e => e.severity === "warning").length ?? 0;
  const criticalCount = errorCount - warningCount;

  const TABS: { id: Tab; label: string; icon: React.ReactNode; badge?: number }[] = useMemo(() => {
    const overview = { id: "overview" as const, label: "Overview", icon: <Activity size={14} /> };
    if (!isPro) return [overview];
    return [
      overview,
      { id: "analytics" as const, label: "Analytics", icon: <BarChart3 size={14} /> },
      {
        id: "monitoring" as const,
        label: "Monitoring",
        icon: <Shield size={14} />,
        badge: criticalCount > 0 ? criticalCount : undefined,
      },
      { id: "activity" as const, label: "Activity", icon: <List size={14} /> },
      { id: "versions" as const, label: "Versions", icon: <GitBranch size={14} /> },
      { id: "webhooks" as const, label: "Webhooks", icon: <WebhookIcon size={14} /> },
      { id: "security" as const, label: "Security", icon: <Lock size={14} /> },
      { id: "integrations" as const, label: "Integrations", icon: <Link2 size={14} /> },
    ];
  }, [isPro, criticalCount]);

  useEffect(() => {
    if (!isPro && (tab === "analytics" || tab === "monitoring" || tab === "activity" || tab === "versions" || tab === "webhooks" || tab === "security" || tab === "integrations")) setTab("overview");
  }, [isPro, tab]);

  useEffect(() => {
    if (!isPro) return;
    const t = searchParams.get("tab");
    if (t === "overview" || t === "analytics" || t === "monitoring" || t === "activity" || t === "versions" || t === "webhooks" || t === "security" || t === "integrations") setTab(t as Tab);
  }, [searchParams, isPro]);

  // Load integrations (registry connections) when tab is active
  useEffect(() => {
    if (tab !== "integrations" || !email) return;
    setIntegrationsLoading(true);
    setIntegrationsError(null);
    import("../lib/registryApi").then(({ listRegistryConnections }) => {
      listRegistryConnections({ email })
        .then(setIntegrations)
        .catch(e => setIntegrationsError(e instanceof Error ? e.message : "Failed to load integrations"))
        .finally(() => setIntegrationsLoading(false));
    });
  }, [tab, email]);

  // Load webhooks when tab is active
  useEffect(() => {
    if (tab !== "webhooks" || !email) return;
    setWebhooksLoading(true);
    setWebhooksError(null);
    listWebhooks(email)
      .then(setWebhooks)
      .catch(e => setWebhooksError(e.message ?? "Failed to load webhooks"))
      .finally(() => setWebhooksLoading(false));
  }, [tab, email]);

  // Load versions when tab is selected or selectedServer changes
  useEffect(() => {
    if (tab !== "versions" || !selectedServer || !email) return;
    setVersionsLoading(true);
    setVersionsError(null);
    listMcpVersions(selectedServer, email)
      .then(setVersions)
      .catch(e => setVersionsError(e.message ?? "Failed to load versions"))
      .finally(() => setVersionsLoading(false));
  }, [tab, selectedServer, email]);

  // Load security data when security tab is active
  useEffect(() => {
    if (tab !== "security") return;
    // API keys
    setSecApiKeysLoading(true);
    listApiKeys()
      .then(setSecApiKeys)
      .catch(() => setSecApiKeys([]))
      .finally(() => setSecApiKeysLoading(false));
    // Audit logs
    setSecAuditLoading(true);
    getAuditLogs({ limit: 50, days: secAuditDays })
      .then(setSecAuditLogs)
      .catch(() => setSecAuditLogs([]))
      .finally(() => setSecAuditLoading(false));
    // IP allowlist
    setSecIpLoading(true);
    listIpAllowlist()
      .then(setSecIpList)
      .catch(() => setSecIpList([]))
      .finally(() => setSecIpLoading(false));
    // 2FA status
    get2faStatus()
      .then(setSec2fa)
      .catch(() => setSec2fa({ enabled: false, configured: false }));
  }, [tab, secAuditDays]);

  return (
    <div className="min-h-screen bg-background text-foreground flex flex-col">
      <TopHeader />

      {/* Toast */}
      <AnimatePresence>
        {toast && (
          <motion.div
            initial={{ opacity: 0, y: -20 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -20 }}
            className="fixed top-20 left-1/2 -translate-x-1/2 z-50 px-6 py-3 rounded-xl bg-emerald-500 text-white text-sm font-medium shadow-lg"
          >
            {toast}
          </motion.div>
        )}
      </AnimatePresence>

      <main className="flex-1 max-w-5xl mx-auto w-full px-4 py-10">

        {/* ── Page header ── */}
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-2xl font-bold">Dashboard</h1>
            {email && <p className="text-sm text-muted-foreground mt-0.5">{email}</p>}
          </div>
          <div className="flex items-center gap-2">
            {allMcps.length > 0 && (tab === "analytics" || tab === "monitoring" || tab === "versions") && (
              <McpSelector mcps={allMcps} selected={selectedServer} onChange={setSelectedServer} />
            )}
            {isPro ? (
              <span className="flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-gradient-to-r from-blue-500 to-violet-500 text-white text-sm font-bold">
                <Crown size={12} className="fill-white" /> Pro
              </span>
            ) : (
              <span className="px-3 py-1.5 rounded-full bg-muted text-muted-foreground text-sm font-medium border border-border">
                Free
              </span>
            )}
          </div>
        </div>

        {TABS.length > 1 && (
        <div className="flex items-center gap-1 p-1 rounded-xl bg-muted/50 border border-border mb-8 w-fit">
          {TABS.map(t => (
            <button
              key={t.id}
              onClick={() => setTab(t.id)}
              className={`relative flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-all ${
                tab === t.id
                  ? "bg-card text-foreground shadow-sm border border-border"
                  : "text-muted-foreground hover:text-foreground"
              }`}
            >
              {t.icon}
              {t.label}
              {t.badge != null && t.badge > 0 && (
                <span className="absolute -top-1 -right-1 w-4 h-4 flex items-center justify-center rounded-full bg-red-500 text-white text-[9px] font-bold">
                  {t.badge > 9 ? "9+" : t.badge}
                </span>
              )}
            </button>
          ))}
        </div>
        )}

        {!sessionChecked ? (
          <div className="flex items-center justify-center py-20 gap-3 text-muted-foreground">
            <RefreshCw size={18} className="animate-spin" /> Checking sign-in…
          </div>
        ) : !email ? (
          <div className="text-center py-20">
            <p className="text-muted-foreground mb-4">Sign in to view your dashboard.</p>
            <Link to="/" className="text-sm font-medium text-primary underline">Go home</Link>
          </div>
        ) : (
          <AnimatePresence mode="wait">

            {/* ══════════════════════════════════════════════════
                TAB: OVERVIEW
            ══════════════════════════════════════════════════ */}
            {tab === "overview" && (
              <motion.div key="overview" initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }} className="space-y-8">

                {refreshing && (
                  <p className="text-xs text-muted-foreground flex items-center gap-2 -mb-4">
                    <RefreshCw size={12} className="animate-spin" /> Updating stats…
                  </p>
                )}

                {/* Usage at a glance */}
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                  <StatCard
                    label="Active MCPs"
                    value={usageSummary?.active_mcps ?? activeMcps.length}
                    icon={<Zap size={16} />}
                    accent="blue"
                    pending={!statsReady}
                  />
                  <StatCard
                    label="Calls today"
                    value={usageSummary?.calls_today ?? 0}
                    icon={<Activity size={16} />}
                    accent="green"
                    pending={!statsReady}
                  />
                  <StatCard
                    label="Total calls"
                    value={usageSummary?.total_calls ?? 0}
                    icon={<TrendingUp size={16} />}
                    accent="violet"
                    pending={!statsReady}
                  />
                </div>

                {/* Plan banner */}
                <section className="rounded-2xl border border-border bg-card p-6">
                  <h2 className="font-semibold mb-4 flex items-center gap-2 text-sm">
                    <Crown size={15} className="text-blue-500" /> Plan
                  </h2>
                  {isPro ? (
                    <div className="flex items-center gap-4">
                      <div className="flex-1">
                        <p className="text-sm text-muted-foreground">
                          Pro{status?.current_period_end ? <> — renews <span className="font-medium text-foreground">{formatDate(status.current_period_end)}</span></> : ""}
                        </p>
                        <p className="text-xs text-muted-foreground mt-1">
                          Unlimited conversions · MCPs never expire · Analytics + monitoring
                        </p>
                        <p className="text-xs text-muted-foreground mt-1 tabular-nums">
                          {conversionsUsed} conversion{conversionsUsed !== 1 ? "s" : ""} this month
                        </p>
                      </div>
                      <Link to="/pricing" className="text-sm text-blue-500 hover:underline font-medium shrink-0">Manage</Link>
                    </div>
                  ) : (
                    <>
                      <div className="flex items-center justify-between text-sm mb-2">
                        <span className="text-muted-foreground">Conversions this month</span>
                        <span className="font-semibold tabular-nums">{conversionsUsed} / {monthlyLimit}</span>
                      </div>
                      <div className="h-2 rounded-full bg-muted overflow-hidden mb-4">
                        <div
                          className={`h-full rounded-full transition-all ${conversionsUsed >= monthlyLimit ? "bg-red-500" : "bg-gradient-to-r from-blue-500 to-violet-500"}`}
                          style={{ width: `${Math.min(100, (conversionsUsed / monthlyLimit) * 100)}%` }}
                        />
                      </div>
                      {showUpgradeNudge && (
                        <div className="mb-3 flex items-start gap-2 text-xs text-amber-800 dark:text-amber-300 bg-amber-50 dark:bg-amber-950/30 border border-amber-200 dark:border-amber-700/50 rounded-lg px-3 py-2">
                          <AlertTriangle size={12} className="shrink-0 mt-0.5 text-amber-600" />
                          <span>
                            {conversionsLeft === 1 ? "Last conversion remaining this month!" : `Only ${conversionsLeft} conversions left this month`}
                            {" — "}
                            <button onClick={handleUpgrade} className="font-semibold underline">upgrade to Pro</button> for unlimited.
                          </span>
                        </div>
                      )}
                      <div className="flex gap-3">
                        <button onClick={handleUpgrade} className="flex items-center gap-2 px-5 py-2.5 rounded-xl bg-gradient-to-r from-blue-500 to-violet-500 text-white text-sm font-semibold hover:opacity-90 transition shadow-sm">
                          <Crown size={14} />Upgrade to Pro — $20/mo
                        </button>
                        <Link to="/pricing" className="flex items-center gap-2 px-5 py-2.5 rounded-xl border border-border text-sm font-medium hover:bg-muted transition-colors">
                          See plans
                        </Link>
                      </div>
                    </>
                  )}
                </section>

                {/* Active MCPs */}
                <section>
                  <h2 className="font-semibold mb-4 flex items-center gap-2 text-sm">
                    <Zap size={15} className="text-blue-500" /> Active MCPs
                    <span className="ml-auto font-normal text-muted-foreground">{activeMcps.length}</span>
                  </h2>
                  {activeMcps.length === 0 ? (
                    <div className="rounded-2xl border border-dashed border-border p-10 text-center">
                      <Zap size={22} className="text-muted-foreground mx-auto mb-3 opacity-40" />
                      <p className="text-sm text-muted-foreground mb-4">No active MCPs yet.</p>
                      <Link to="/" className="inline-flex items-center gap-2 px-5 py-2.5 rounded-xl bg-primary text-primary-foreground text-sm font-semibold hover:opacity-90 transition">
                        <Zap size={14} /> Convert an API
                      </Link>
                    </div>
                  ) : (
                    <div className="space-y-3">
                      {activeMcps.map(mcp => (
                        <McpCard
                          key={mcp.id}
                          mcp={mcp}
                          showUsageAnalytics={isPro}
                          onViewAnalytics={() => switchToAnalytics(mcp.server_id)}
                          email={email}
                        />
                      ))}
                    </div>
                  )}
                </section>

                {expiredMcps.length > 0 && (
                  <section>
                    <h2 className="font-semibold mb-4 flex items-center gap-2 text-sm text-muted-foreground">
                      <Clock size={15} /> Expired
                      <span className="ml-auto font-normal">{expiredMcps.length}</span>
                    </h2>
                    <div className="space-y-3">
                      {expiredMcps.map(mcp => (
                        <McpCard
                          key={mcp.id}
                          mcp={mcp}
                          showUsageAnalytics={isPro}
                          onViewAnalytics={() => switchToAnalytics(mcp.server_id)}
                          email={email}
                        />
                      ))}
                    </div>
                  </section>
                )}

                {/* Referral program */}
                <section className="rounded-2xl border border-border bg-card p-6">
                  <h2 className="font-semibold mb-1 flex items-center gap-2 text-sm">
                    <Gift size={15} className="text-violet-500" /> Refer a friend, get a free conversion
                  </h2>
                  <p className="text-xs text-muted-foreground mb-4">Share your link. When a friend signs up and converts their first API, you both get a free extra conversion.</p>
                  {referralStats ? (
                    <div className="space-y-3">
                      <div className="flex gap-2">
                        <input
                          readOnly
                          value={referralStats.referral_url}
                          className="flex-1 text-xs px-3 py-2 rounded-lg border border-border bg-muted text-foreground font-mono focus:outline-none"
                        />
                        <button
                          onClick={() => {
                            navigator.clipboard.writeText(referralStats.referral_url);
                            setReferralCopied(true);
                            setTimeout(() => setReferralCopied(false), 2000);
                          }}
                          className="shrink-0 px-3 py-2 rounded-lg border border-border text-xs font-medium hover:bg-muted transition-colors"
                        >
                          {referralCopied ? "✓ Copied!" : "Copy"}
                        </button>
                      </div>
                      <div className="flex gap-4 text-xs text-muted-foreground">
                        <span><span className="font-semibold text-foreground">{referralStats.total_referrals}</span> referral{referralStats.total_referrals !== 1 ? "s" : ""}</span>
                        <span><span className="font-semibold text-foreground">{referralStats.bonus_conversions}</span> bonus conversion{referralStats.bonus_conversions !== 1 ? "s" : ""} earned</span>
                      </div>
                    </div>
                  ) : (
                    <div className="h-8 rounded-lg bg-muted animate-pulse w-64" />
                  )}
                </section>
              </motion.div>
            )}

            {/* ══════════════════════════════════════════════════
                TAB: ANALYTICS
            ══════════════════════════════════════════════════ */}
            {tab === "analytics" && isPro && (
              <motion.div key="analytics" initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }} className="space-y-6">
                {!selectedServer ? (
                  <p className="text-center text-muted-foreground py-20">Select an MCP to view analytics.</p>
                ) : loadingUsage ? (
                  <div className="flex items-center justify-center py-20 gap-3 text-muted-foreground">
                    <RefreshCw size={16} className="animate-spin" /> Loading analytics…
                  </div>
                ) : !usage ? (
                  <p className="text-center text-muted-foreground py-20">No data yet for this MCP.</p>
                ) : (
                  <>
                    {/* KPI cards */}
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                      <StatCard label="Total calls" value={usage.total_calls.toLocaleString()} icon={<Activity size={16} />} accent="blue" pulse />
                      <StatCard label="Success rate" value={`${(100 - usage.error_rate).toFixed(1)}%`} icon={<CheckCircle2 size={16} />} accent={usage.error_rate > 5 ? "red" : usage.error_rate > 1 ? "amber" : "green"} />
                      <StatCard label="Avg response" value={usage.avg_response_ms < 1000 ? `${usage.avg_response_ms}ms` : `${(usage.avg_response_ms / 1000).toFixed(1)}s`} icon={<Timer size={16} />} accent={usage.avg_response_ms > 2000 ? "red" : usage.avg_response_ms > 500 ? "amber" : "violet"} />
                      <StatCard label="Unique tools" value={usage.top_tools.length} icon={<Zap size={16} />} accent="blue" />
                    </div>

                    {/* Daily chart */}
                    <div className="rounded-2xl border border-border bg-card p-6">
                      <div className="flex items-center justify-between mb-4">
                        <h3 className="font-semibold text-sm">Calls — last 30 days</h3>
                        <div className="flex items-center gap-3 text-xs text-muted-foreground">
                          <span className="flex items-center gap-1.5"><span className="w-2.5 h-2.5 rounded-sm bg-blue-500/70 inline-block" />Success</span>
                          <span className="flex items-center gap-1.5"><span className="w-2.5 h-2.5 rounded-sm bg-red-400/80 inline-block" />Errors</span>
                        </div>
                      </div>
                      <StackedBarChart daily={usage.daily} />
                      {usage.daily.length > 0 && (
                        <div className="flex justify-between mt-2 text-[10px] text-muted-foreground">
                          <span>{usage.daily[0]?.day}</span>
                          <span>{usage.daily[usage.daily.length - 1]?.day}</span>
                        </div>
                      )}
                    </div>

                    {/* Client breakdown */}
                    {usage.clients?.length > 0 && (
                      <div className="rounded-2xl border border-border bg-card p-6">
                        <h3 className="font-semibold text-sm mb-4">Calls by AI client</h3>
                        <div className="flex flex-wrap gap-3">
                          {usage.clients.map(c => {
                            const icons: Record<string, string> = {
                              "Claude Desktop": "🖥️",
                              "claude.ai": "🌐",
                              "Cursor": "🎯",
                              "Cline": "⚡",
                              "Windsurf": "🏄",
                              "Copilot": "🤖",
                              "Continue": "▶️",
                              "Zed": "⚡",
                              "Sourcegraph": "🔍",
                              "Other": "🔧",
                              "Unknown": "❓",
                            };
                            const maxCalls = usage.clients[0]?.calls ?? 1;
                            const pct = Math.round((c.calls / maxCalls) * 100);
                            return (
                              <div key={c.client} className="flex-1 min-w-[140px] rounded-xl border border-border bg-muted/30 p-4">
                                <div className="flex items-center gap-2 mb-2">
                                  <span className="text-lg">{icons[c.client] ?? "🔧"}</span>
                                  <span className="text-sm font-medium text-foreground truncate">{c.client}</span>
                                </div>
                                <div className="text-2xl font-bold text-foreground tabular-nums">{c.calls.toLocaleString()}</div>
                                <div className="text-[10px] text-muted-foreground mt-0.5">calls in {usage.period_days}d</div>
                                <div className="mt-2 h-1.5 rounded-full bg-muted overflow-hidden">
                                  <div className="h-full rounded-full bg-blue-500/70" style={{ width: `${pct}%` }} />
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    )}

                    {/* Top tools */}
                    {usage.top_tools.length > 0 && (
                      <div className="rounded-2xl border border-border bg-card p-6">
                        <h3 className="font-semibold text-sm mb-4">Top tools</h3>
                        <div className="space-y-3">
                          {usage.top_tools.map((t, i) => {
                            const maxCalls = usage.top_tools[0]?.calls ?? 1;
                            const errPct = t.calls > 0 ? (t.errors / t.calls) * 100 : 0;
                            return (
                              <div key={t.tool} className="flex items-center gap-3">
                                <span className="text-xs text-muted-foreground w-4 text-right shrink-0">{i + 1}</span>
                                <code className="text-xs font-mono text-foreground w-44 truncate shrink-0">{t.tool}</code>
                                <div className="flex-1 h-2 rounded-full bg-muted overflow-hidden">
                                  <div
                                    className="h-full rounded-full bg-blue-500/70"
                                    style={{ width: `${(t.calls / maxCalls) * 100}%` }}
                                  />
                                </div>
                                <span className="text-xs tabular-nums text-foreground w-16 text-right shrink-0">
                                  {t.calls.toLocaleString()} calls
                                </span>
                                {t.errors > 0 && (
                                  <span className="text-xs text-red-500 tabular-nums w-16 text-right shrink-0">
                                    {errPct.toFixed(1)}% err
                                  </span>
                                )}
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    )}
                  </>
                )}
              </motion.div>
            )}

            {/* ══════════════════════════════════════════════════
                TAB: MONITORING
            ══════════════════════════════════════════════════ */}
            {tab === "monitoring" && isPro && (
              <motion.div key="monitoring" initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }} className="space-y-6">
                {!selectedServer ? (
                  <p className="text-center text-muted-foreground py-20">Select an MCP to view monitoring.</p>
                ) : (
                  <>
                    {/* Toolbar */}
                    <div className="flex items-center gap-3 flex-wrap">
                      <h2 className="font-semibold text-sm flex items-center gap-2">
                        <Radio size={14} className="text-blue-500" /> Live monitoring
                      </h2>
                      <div className="ml-auto flex items-center gap-2">
                        {/* Days filter */}
                        {[1, 3, 7, 14].map(d => (
                          <button
                            key={d}
                            onClick={() => setMonitorDays(d)}
                            className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-colors border ${
                              monitorDays === d
                                ? "bg-blue-500 text-white border-blue-500"
                                : "border-border text-muted-foreground hover:text-foreground hover:bg-muted"
                            }`}
                          >
                            {d}d
                          </button>
                        ))}
                        {/* Auto-refresh toggle */}
                        <button
                          onClick={() => setAutoRefresh(a => !a)}
                          className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium border transition-colors ${
                            autoRefresh
                              ? "bg-emerald-500/10 border-emerald-500/40 text-emerald-600 dark:text-emerald-400"
                              : "border-border text-muted-foreground hover:text-foreground hover:bg-muted"
                          }`}
                        >
                          {autoRefresh ? <Wifi size={12} /> : <WifiOff size={12} />}
                          {autoRefresh ? "Live" : "Auto-refresh"}
                        </button>
                        {/* Manual refresh */}
                        <button
                          onClick={loadMonitoring}
                          disabled={loadingMonitor}
                          className="p-1.5 rounded-lg border border-border text-muted-foreground hover:text-foreground hover:bg-muted transition-colors disabled:opacity-40"
                        >
                          <RefreshCw size={13} className={loadingMonitor ? "animate-spin" : ""} />
                        </button>
                      </div>
                    </div>

                    {/* Sticky alert banner when server is currently down/degraded */}
                    {(() => {
                      const sel = allMcps.find(m => m.server_id === selectedServer);
                      if (!sel || !sel.last_health_error || sel.health_status === "healthy" || sel.health_status == null) return null;
                      const { message, hint } = classifyHealthError(sel.last_health_error);
                      const isDown = sel.health_status === "down";
                      return (
                        <div className={`flex items-start gap-3 rounded-xl border px-4 py-3 text-sm ${
                          isDown
                            ? "bg-red-50 dark:bg-red-950/30 border-red-200 dark:border-red-800 text-red-700 dark:text-red-300"
                            : "bg-amber-50 dark:bg-amber-950/30 border-amber-200 dark:border-amber-800 text-amber-700 dark:text-amber-300"
                        }`}>
                          <AlertTriangle size={16} className="shrink-0 mt-0.5" />
                          <div>
                            <p className="font-semibold">{isDown ? "MCP is currently down" : "MCP is degraded"} — {message}</p>
                            {hint && <p className="text-xs mt-0.5 opacity-80">{hint}</p>}
                          </div>
                        </div>
                      );
                    })()}

                    {loadingMonitor && !errorLogs ? (
                      <div className="flex items-center justify-center py-20 gap-3 text-muted-foreground">
                        <RefreshCw size={16} className="animate-spin" /> Loading monitoring data…
                      </div>
                    ) : (
                      <>
                        {/* ── Uptime KPIs ── */}
                        {uptimeStats && (
                          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                            {/* Uptime donut */}
                            <div className="rounded-2xl border border-border bg-card p-5 flex items-center gap-4 col-span-1">
                              <UptimeDonut pct={uptimeStats.uptime_pct} />
                              <div>
                                <p className="text-sm font-bold text-foreground">Uptime</p>
                                <p className="text-xs text-muted-foreground mt-0.5">Last {uptimeStats.period_days}d</p>
                                <p className={`text-xs font-semibold mt-1 ${
                                  uptimeStats.uptime_pct >= 99.9 ? "text-emerald-500"
                                  : uptimeStats.uptime_pct >= 99 ? "text-blue-500"
                                  : uptimeStats.uptime_pct >= 95 ? "text-amber-500"
                                  : "text-red-500"
                                }`}>
                                  {uptimeStats.uptime_pct >= 99.9 ? "Excellent" : uptimeStats.uptime_pct >= 99 ? "Good" : uptimeStats.uptime_pct >= 95 ? "Degraded" : "Poor"}
                                </p>
                              </div>
                            </div>

                            <StatCard
                              label="Typical speed"
                              value={uptimeStats.p50_ms < 1000 ? `${uptimeStats.p50_ms}ms` : `${(uptimeStats.p50_ms / 1000).toFixed(1)}s`}
                              sub="most calls finish this fast"
                              icon={<TrendingUp size={16} />}
                              accent={uptimeStats.p50_ms > 2000 ? "amber" : "green"}
                            />
                            <StatCard
                              label="Worst-case speed"
                              value={uptimeStats.p95_ms < 1000 ? `${uptimeStats.p95_ms}ms` : `${(uptimeStats.p95_ms / 1000).toFixed(1)}s`}
                              sub="slowest 1-in-20 calls"
                              icon={<Timer size={16} />}
                              accent={uptimeStats.p95_ms > 2000 ? "red" : uptimeStats.p95_ms > 500 ? "amber" : "green"}
                            />
                            <StatCard
                              label="Success rate"
                              value={uptimeStats.total_calls > 0 ? `${(((uptimeStats.total_calls - uptimeStats.failures) / uptimeStats.total_calls) * 100).toFixed(1)}%` : "—"}
                              sub={uptimeStats.failures === 0 ? "no errors" : `${uptimeStats.failures} failed`}
                              icon={<AlertCircle size={16} />}
                              accent={uptimeStats.failures === 0 ? "green" : uptimeStats.failures < 5 ? "amber" : "red"}
                            />
                          </div>
                        )}

                        {/* ── Probe health: current status + 24h heatmap ── */}
                        {probeStats && (
                          <div className="rounded-2xl border border-border bg-card p-6 space-y-5">
                            <div className="flex items-center justify-between flex-wrap gap-3">
                              <h3 className="font-semibold text-sm flex items-center gap-2">
                                <Radio size={14} className="text-blue-500" /> Infrastructure health probes
                              </h3>
                              <div className="flex items-center gap-3 text-xs text-muted-foreground">
                                <span>{probeStats.total_probes} probes · {probeStats.period_days}d</span>
                                {probeStats.incidents > 0 && (
                                  <span className="px-2 py-0.5 rounded-full bg-red-100 dark:bg-red-950/40 text-red-600 dark:text-red-400 font-medium">
                                    {probeStats.incidents} incident{probeStats.incidents !== 1 ? "s" : ""}
                                  </span>
                                )}
                              </div>
                            </div>

                            {/* Probe KPI row */}
                            <div className="grid grid-cols-3 gap-3">
                              {[
                                { label: "Probe uptime", value: `${probeStats.uptime_pct.toFixed(2)}%`, color: probeStats.uptime_pct >= 99.9 ? "text-emerald-500" : probeStats.uptime_pct >= 99 ? "text-blue-500" : "text-amber-500" },
                                { label: "Median latency", value: probeStats.p50_ms ? `${probeStats.p50_ms}ms` : "—", color: "text-foreground" },
                                { label: "P95 latency", value: probeStats.p95_ms ? `${probeStats.p95_ms}ms` : "—", color: (probeStats.p95_ms ?? 0) > 2000 ? "text-red-500" : "text-foreground" },
                              ].map(kpi => (
                                <div key={kpi.label} className="rounded-xl border border-border p-3 text-center">
                                  <p className={`text-lg font-bold tabular-nums ${kpi.color}`}>{kpi.value}</p>
                                  <p className="text-[10px] text-muted-foreground mt-0.5">{kpi.label}</p>
                                </div>
                              ))}
                            </div>

                            {/* 24h heatmap */}
                            {probeStats.hourly.length > 0 && (
                              <div>
                                <p className="text-xs text-muted-foreground mb-2">Last 24h — hourly probe status</p>
                                <div className="flex gap-1 flex-wrap">
                                  {probeStats.hourly.map((h, i) => (
                                    <div
                                      key={i}
                                      title={`${h.hour} · ${h.uptime_pct}% uptime (${h.total} probes)`}
                                      className={`w-6 h-6 rounded flex-shrink-0 cursor-default transition-opacity hover:opacity-80 ${
                                        h.uptime_pct >= 100 ? "bg-emerald-400 dark:bg-emerald-500"
                                        : h.uptime_pct >= 50 ? "bg-amber-400 dark:bg-amber-500"
                                        : "bg-red-400 dark:bg-red-500"
                                      }`}
                                    />
                                  ))}
                                </div>
                                <div className="flex items-center gap-3 mt-2 text-[10px] text-muted-foreground">
                                  <span className="flex items-center gap-1"><span className="w-2.5 h-2.5 rounded-sm bg-emerald-400 inline-block" /> Healthy</span>
                                  <span className="flex items-center gap-1"><span className="w-2.5 h-2.5 rounded-sm bg-amber-400 inline-block" /> Degraded</span>
                                  <span className="flex items-center gap-1"><span className="w-2.5 h-2.5 rounded-sm bg-red-400 inline-block" /> Down</span>
                                </div>
                              </div>
                            )}
                          </div>
                        )}

                        {/* ── Health probe history feed ── */}
                        {healthHistory && healthHistory.length > 0 && (
                          <div className="rounded-2xl border border-border bg-card overflow-hidden">
                            <div className="px-6 py-4 border-b border-border">
                              <h3 className="font-semibold text-sm flex items-center gap-2">
                                <Clock size={14} className="text-muted-foreground" /> Probe history
                                <span className="text-xs font-normal text-muted-foreground ml-1">(last {healthHistory.length} checks)</span>
                              </h3>
                            </div>
                            <div className="divide-y divide-border/50 max-h-64 overflow-y-auto">
                              {healthHistory.map(row => {
                                const checkedAt = new Date(row.checked_at).toISOString();
                                return (
                                  <div key={row.id} className="flex items-center gap-3 px-5 py-2.5 text-xs hover:bg-muted/30 transition-colors">
                                    <span className={`flex-shrink-0 w-2 h-2 rounded-full ${row.status === "healthy" ? "bg-emerald-500" : row.status === "degraded" ? "bg-amber-500" : "bg-red-500"}`} />
                                    <span className={`font-medium w-16 ${row.status === "healthy" ? "text-emerald-600 dark:text-emerald-400" : row.status === "degraded" ? "text-amber-600 dark:text-amber-400" : "text-red-600 dark:text-red-400"}`}>
                                      {row.status}
                                    </span>
                                    <span className="text-muted-foreground flex-1 truncate">{row.error_message ?? "OK"}</span>
                                    <span className="text-muted-foreground tabular-nums flex-shrink-0">{row.response_time_ms != null ? `${row.response_time_ms}ms` : "—"}</span>
                                    <span className="text-muted-foreground flex-shrink-0">{formatRelative(checkedAt)}</span>
                                  </div>
                                );
                              })}
                            </div>
                          </div>
                        )}

                        {/* ── Error categories ── */}
                        {uptimeStats && uptimeStats.error_categories.length > 0 && (
                          <div className="rounded-2xl border border-border bg-card p-6">
                            <h3 className="font-semibold text-sm mb-4 flex items-center gap-2">
                              <AlertTriangle size={14} className="text-amber-500" /> Error breakdown
                            </h3>
                            <div className="space-y-2.5">
                              {uptimeStats.error_categories.map(cat => {
                                const total = uptimeStats.error_categories.reduce((s, c) => s + c.count, 0);
                                const pct = total > 0 ? (cat.count / total) * 100 : 0;
                                const isTimeout = cat.category === "Timeout";
                                const isAuth = cat.category === "Auth error";
                                return (
                                  <div key={cat.category} className="flex items-center gap-3">
                                    <span className="text-xs text-muted-foreground w-28 shrink-0">{cat.category}</span>
                                    <div className="flex-1 h-2 rounded-full bg-muted overflow-hidden">
                                      <div
                                        className={`h-full rounded-full ${isTimeout ? "bg-amber-400" : isAuth ? "bg-purple-400" : "bg-red-400"}`}
                                        style={{ width: `${pct}%` }}
                                      />
                                    </div>
                                    <span className="text-xs tabular-nums text-foreground w-8 text-right shrink-0">{cat.count}</span>
                                    <span className="text-xs text-muted-foreground w-10 text-right shrink-0">{pct.toFixed(0)}%</span>
                                  </div>
                                );
                              })}
                            </div>
                          </div>
                        )}

                        {/* ── Alert Settings ── */}
                        <div className="rounded-2xl border border-border bg-card overflow-hidden">
                          <div className="px-6 py-4 border-b border-border">
                            <h3 className="font-semibold text-sm flex items-center gap-2">
                              <Bell size={14} className="text-indigo-500" /> Alert Settings
                            </h3>
                            <p className="text-xs text-muted-foreground mt-0.5">Choose when to receive email notifications about this MCP.</p>
                          </div>
                          <div className="p-6 space-y-4">
                            {!alertPrefs ? (
                              <p className="text-sm text-muted-foreground">Loading preferences…</p>
                            ) : (
                              <>
                                <label className="flex items-center gap-3 cursor-pointer select-none">
                                  <input
                                    type="checkbox"
                                    className="w-4 h-4 accent-indigo-500"
                                    checked={alertPrefs.alert_on_down}
                                    onChange={e => setAlertPrefs(p => p ? { ...p, alert_on_down: e.target.checked } : p)}
                                  />
                                  <span className="text-sm font-medium">Email me when my MCP goes <span className="text-red-500">DOWN</span></span>
                                </label>
                                <label className="flex items-center gap-3 cursor-pointer select-none">
                                  <input
                                    type="checkbox"
                                    className="w-4 h-4 accent-indigo-500"
                                    checked={alertPrefs.alert_on_degraded}
                                    onChange={e => setAlertPrefs(p => p ? { ...p, alert_on_degraded: e.target.checked } : p)}
                                  />
                                  <span className="text-sm font-medium">Email me when my MCP is <span className="text-amber-500">DEGRADED</span></span>
                                </label>
                                <label className="flex items-center gap-3 cursor-pointer select-none">
                                  <input
                                    type="checkbox"
                                    className="w-4 h-4 accent-indigo-500"
                                    checked={alertPrefs.weekly_summary}
                                    onChange={e => setAlertPrefs(p => p ? { ...p, weekly_summary: e.target.checked } : p)}
                                  />
                                  <span className="text-sm font-medium">Weekly summary report</span>
                                </label>
                                {/* Slack webhook */}
                                <div className="pt-2 border-t border-border space-y-2">
                                  <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">Slack alerts</p>
                                  <p className="text-xs text-muted-foreground">Paste your Slack incoming webhook URL to get alerts in Slack when this MCP goes down or recovers.</p>
                                  <div className="flex gap-2">
                                    <input
                                      type="url"
                                      placeholder="https://hooks.slack.com/services/..."
                                      value={alertPrefs.slack_webhook_url ?? ""}
                                      onChange={e => setAlertPrefs(p => p ? { ...p, slack_webhook_url: e.target.value || null } : p)}
                                      className="flex-1 text-xs px-3 py-2 rounded-lg border border-border bg-background text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/30"
                                    />
                                    <button
                                      type="button"
                                      disabled={!alertPrefs.slack_webhook_url || slackTesting}
                                      onClick={async () => {
                                        if (!email) return;
                                        setSlackTesting(true);
                                        setSlackTestResult(null);
                                        try {
                                          await testSlackWebhook(email);
                                          setSlackTestResult("ok");
                                        } catch {
                                          setSlackTestResult("error");
                                        } finally {
                                          setSlackTesting(false);
                                          setTimeout(() => setSlackTestResult(null), 3000);
                                        }
                                      }}
                                      className="shrink-0 px-3 py-2 rounded-lg border border-border text-xs font-medium hover:bg-muted transition-colors disabled:opacity-50"
                                    >
                                      {slackTesting ? <RefreshCw size={12} className="animate-spin" /> : slackTestResult === "ok" ? "✓ Sent!" : slackTestResult === "error" ? "✗ Failed" : "Test"}
                                    </button>
                                  </div>
                                  <p className="text-[10px] text-muted-foreground">
                                    Get a webhook URL at <a href="https://api.slack.com/messaging/webhooks" target="_blank" rel="noreferrer" className="underline">api.slack.com/messaging/webhooks</a>
                                  </p>
                                </div>

                                {/* Discord webhook */}
                                <div className="pt-2 border-t border-border space-y-2">
                                  <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">Discord alerts</p>
                                  <p className="text-xs text-muted-foreground">Paste your Discord channel webhook URL to get alerts in Discord when this MCP goes down or recovers.</p>
                                  <div className="flex gap-2">
                                    <input
                                      type="url"
                                      placeholder="https://discord.com/api/webhooks/..."
                                      value={alertPrefs.discord_webhook_url ?? ""}
                                      onChange={e => setAlertPrefs(p => p ? { ...p, discord_webhook_url: e.target.value || null } : p)}
                                      className="flex-1 text-xs px-3 py-2 rounded-lg border border-border bg-background text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/30"
                                    />
                                    <button
                                      type="button"
                                      disabled={!alertPrefs.discord_webhook_url || discordTesting}
                                      onClick={async () => {
                                        if (!email) return;
                                        setDiscordTesting(true);
                                        setDiscordTestResult(null);
                                        try {
                                          await testDiscordWebhook(email);
                                          setDiscordTestResult("ok");
                                        } catch {
                                          setDiscordTestResult("error");
                                        } finally {
                                          setDiscordTesting(false);
                                          setTimeout(() => setDiscordTestResult(null), 3000);
                                        }
                                      }}
                                      className="shrink-0 px-3 py-2 rounded-lg border border-border text-xs font-medium hover:bg-muted transition-colors disabled:opacity-50"
                                    >
                                      {discordTesting ? <RefreshCw size={12} className="animate-spin" /> : discordTestResult === "ok" ? "✓ Sent!" : discordTestResult === "error" ? "✗ Failed" : "Test"}
                                    </button>
                                  </div>
                                  <p className="text-[10px] text-muted-foreground">
                                    In Discord: channel Settings → Integrations → Webhooks → New Webhook
                                  </p>
                                </div>

                                <div className="pt-1 flex items-center gap-3">
                                  <p className="text-xs text-muted-foreground flex-1">Emails go to: <span className="font-medium text-foreground">{email}</span></p>
                                  <button
                                    disabled={alertPrefsSaving}
                                    onClick={async () => {
                                      if (!email || !alertPrefs) return;
                                      setAlertPrefsSaving(true);
                                      setAlertPrefsSaved(false);
                                      try {
                                        const saved = await updateAlertPreferences(email, {
                                          alert_on_down: alertPrefs.alert_on_down,
                                          alert_on_degraded: alertPrefs.alert_on_degraded,
                                          weekly_summary: alertPrefs.weekly_summary,
                                          slack_webhook_url: alertPrefs.slack_webhook_url,
                                          discord_webhook_url: alertPrefs.discord_webhook_url,
                                        });
                                        setAlertPrefs(saved);
                                        setAlertPrefsSaved(true);
                                        setTimeout(() => setAlertPrefsSaved(false), 2500);
                                      } catch { /* ignore */ } finally {
                                        setAlertPrefsSaving(false);
                                      }
                                    }}
                                    className="flex items-center gap-2 px-4 py-2 rounded-xl bg-primary text-primary-foreground text-sm font-semibold shadow hover:opacity-90 transition-opacity disabled:opacity-50"
                                  >
                                    {alertPrefsSaving ? <RefreshCw size={13} className="animate-spin" /> : alertPrefsSaved ? <CheckCircle2 size={13} /> : null}
                                    {alertPrefsSaved ? "Saved!" : "Save Settings"}
                                  </button>
                                </div>
                              </>
                            )}
                          </div>
                        </div>

                        {/* ── Error log feed ── */}
                        <div className="rounded-2xl border border-border bg-card overflow-hidden">
                          <div className="px-6 py-4 border-b border-border flex items-center gap-3">
                            <h3 className="font-semibold text-sm flex items-center gap-2">
                              <AlertCircle size={14} className="text-red-500" /> Error log
                            </h3>
                            <div className="flex items-center gap-2 ml-auto">
                              {criticalCount > 0 && (
                                <span className="flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-red-100 dark:bg-red-950/50 text-red-700 dark:text-red-400 text-xs font-medium">
                                  <XCircle size={11} /> {criticalCount} error{criticalCount !== 1 ? "s" : ""}
                                </span>
                              )}
                              {warningCount > 0 && (
                                <span className="flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-amber-100 dark:bg-amber-950/50 text-amber-700 dark:text-amber-400 text-xs font-medium">
                                  <AlertTriangle size={11} /> {warningCount} warning{warningCount !== 1 ? "s" : ""}
                                </span>
                              )}
                              {errorCount === 0 && (
                                <span className="flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-emerald-100 dark:bg-emerald-950/50 text-emerald-700 dark:text-emerald-400 text-xs font-medium">
                                  <CheckCircle2 size={11} /> All clear
                                </span>
                              )}
                            </div>
                          </div>

                          <div className="p-4">
                            {errorCount === 0 ? (
                              <div className="flex flex-col items-center justify-center py-12 gap-3">
                                <div className="w-12 h-12 rounded-2xl bg-emerald-500/10 flex items-center justify-center">
                                  <CheckCircle2 size={22} className="text-emerald-500" />
                                </div>
                                <p className="font-semibold text-foreground">No errors in the last {monitorDays} day{monitorDays !== 1 ? "s" : ""}</p>
                                <p className="text-sm text-muted-foreground">Your MCP server is running cleanly.</p>
                              </div>
                            ) : (
                              <div className="space-y-2">
                                {errorLogs!.map((entry, i) => (
                                  <ErrorRow key={entry.id} entry={entry} index={i} />
                                ))}
                              </div>
                            )}
                          </div>
                        </div>
                      </>
                    )}
                  </>
                )}
              </motion.div>
            )}

            {/* ══════════════════════════════════════════════════
                TAB: ACTIVITY
            ══════════════════════════════════════════════════ */}
            {tab === "activity" && isPro && (
              <motion.div key="activity" initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }} className="space-y-4">
                <div className="flex items-center justify-between flex-wrap gap-3">
                  <div>
                    <h2 className="text-base font-semibold text-foreground">Activity Log</h2>
                    <p className="text-xs text-muted-foreground mt-0.5">Every tool call your agent made — in real time.</p>
                  </div>
                  {/* Filter chips */}
                  <div className="flex gap-1.5">
                    {(["all", "success", "error"] as const).map(f => (
                      <button
                        key={f}
                        onClick={() => { setActivityFilter(f); setActivityPage(0); setActivityItems([]); }}
                        className={`px-3 py-1 rounded-full text-xs font-medium border transition-colors ${
                          activityFilter === f
                            ? "bg-foreground text-background border-foreground"
                            : "border-border text-muted-foreground hover:text-foreground"
                        }`}
                      >
                        {f === "all" ? "All" : f === "success" ? "✓ Success" : "✕ Errors"}
                      </button>
                    ))}
                  </div>
                </div>

                {activityError && (
                  <p className="text-sm text-red-500">{activityError}</p>
                )}

                {activityItems.length === 0 && !activityLoading && (
                  <div className="glass-strong rounded-2xl p-10 text-center text-muted-foreground text-sm">
                    No tool calls recorded yet. When your agent uses an MCP, each call appears here.
                  </div>
                )}

                {activityItems.length > 0 && (
                  <div className="glass-strong rounded-2xl overflow-hidden">
                    <table className="w-full text-xs">
                      <thead>
                        <tr className="border-b border-border text-muted-foreground">
                          <th className="text-left px-4 py-3 font-medium">Tool</th>
                          <th className="text-left px-4 py-3 font-medium hidden md:table-cell">Arguments</th>
                          <th className="text-left px-4 py-3 font-medium hidden lg:table-cell">Response</th>
                          <th className="text-left px-4 py-3 font-medium">Status</th>
                          <th className="text-right px-4 py-3 font-medium">Time</th>
                          <th className="text-right px-4 py-3 font-medium">When</th>
                        </tr>
                      </thead>
                      <tbody>
                        {activityItems.map((item, idx) => (
                          <tr
                            key={item.id}
                            className={`border-b border-border/50 last:border-0 transition-colors hover:bg-muted/30 ${idx % 2 === 0 ? "" : "bg-muted/10"}`}
                          >
                            <td className="px-4 py-3 font-mono text-foreground font-medium max-w-[160px] truncate">
                              {item.tool_name}
                            </td>
                            <td className="px-4 py-3 text-muted-foreground max-w-[200px] truncate hidden md:table-cell font-mono">
                              {item.request_summary ?? "—"}
                            </td>
                            <td className="px-4 py-3 text-muted-foreground max-w-[200px] truncate hidden lg:table-cell font-mono">
                              {item.response_summary ?? (item.error_message ?? "—")}
                            </td>
                            <td className="px-4 py-3">
                              {item.success ? (
                                <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-green-100 dark:bg-green-950/40 text-green-700 dark:text-green-400 text-[10px] font-semibold">
                                  <CheckCircle2 size={10} /> OK
                                </span>
                              ) : (
                                <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-red-100 dark:bg-red-950/40 text-red-700 dark:text-red-400 text-[10px] font-semibold">
                                  <XCircle size={10} /> Error
                                </span>
                              )}
                            </td>
                            <td className="px-4 py-3 text-right text-muted-foreground tabular-nums">
                              {item.response_ms != null ? `${item.response_ms}ms` : "—"}
                            </td>
                            <td className="px-4 py-3 text-right text-muted-foreground whitespace-nowrap">
                              {formatRelative(item.called_at)}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}

                {/* Load more */}
                {activityItems.length < activityTotal && (
                  <div className="flex justify-center pt-2">
                    <button
                      onClick={() => setActivityPage(p => p + 1)}
                      disabled={activityLoading}
                      className="px-5 py-2 rounded-xl border border-border text-sm text-muted-foreground hover:text-foreground hover:bg-muted/60 transition disabled:opacity-50"
                    >
                      {activityLoading ? "Loading…" : `Load more (${activityTotal - activityItems.length} remaining)`}
                    </button>
                  </div>
                )}
                {activityLoading && activityItems.length === 0 && (
                  <div className="flex justify-center py-10">
                    <RefreshCw size={18} className="animate-spin text-muted-foreground" />
                  </div>
                )}
              </motion.div>
            )}

            {/* ══════════════════════════════════════════════════
                TAB: VERSIONS
            ══════════════════════════════════════════════════ */}
            {tab === "versions" && isPro && (
              <motion.div key="versions" initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }} className="space-y-6">
                {!selectedServer ? (
                  <p className="text-center text-muted-foreground py-20">Select an MCP to manage versions.</p>
                ) : (
                  <>
                    {/* Header row */}
                    <div className="flex items-center gap-3 flex-wrap">
                      <h2 className="font-semibold text-sm flex items-center gap-2">
                        <GitBranch size={14} className="text-blue-500" /> Spec Versions
                      </h2>
                      <p className="text-xs text-muted-foreground">Upload a new OpenAPI spec, test it, and activate or rollback at any time.</p>
                      <div className="ml-auto">
                        <button
                          onClick={() => setShowUploadForm(v => !v)}
                          className="flex items-center gap-2 px-4 py-2 rounded-xl bg-primary text-primary-foreground text-sm font-semibold shadow hover:opacity-90 transition-opacity"
                        >
                          <Upload size={13} /> Upload new version
                        </button>
                      </div>
                    </div>

                    {/* Upload form */}
                    {showUploadForm && (
                      <motion.div
                        initial={{ opacity: 0, y: -8 }} animate={{ opacity: 1, y: 0 }}
                        className="rounded-2xl border border-border bg-card p-6 space-y-4"
                      >
                        <h3 className="font-semibold text-sm flex items-center gap-2">
                          <FileText size={14} className="text-primary" /> New spec version
                        </h3>
                        <div>
                          <label className="block text-xs font-medium text-muted-foreground mb-1.5">OpenAPI / Swagger spec (paste JSON or YAML)</label>
                          <textarea
                            value={versionUploadText}
                            onChange={e => setVersionUploadText(e.target.value)}
                            rows={10}
                            placeholder={"{\n  \"openapi\": \"3.0.0\",\n  ...\n}"}
                            className="w-full rounded-xl border border-border bg-muted/40 px-4 py-3 text-xs font-mono resize-y focus:outline-none focus:ring-2 focus:ring-primary/40"
                          />
                        </div>
                        <div>
                          <label className="block text-xs font-medium text-muted-foreground mb-1.5">Notes (optional)</label>
                          <input
                            type="text"
                            value={versionUploadNotes}
                            onChange={e => setVersionUploadNotes(e.target.value)}
                            placeholder="e.g. Added /payments endpoint, updated auth scheme"
                            className="w-full rounded-xl border border-border bg-muted/40 px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-primary/40"
                          />
                        </div>
                        <div className="flex items-center gap-3 pt-1">
                          <button
                            disabled={!versionUploadText.trim() || versionUploading}
                            onClick={async () => {
                              if (!email || !selectedServer) return;
                              setVersionUploading(true);
                              try {
                                const v = await uploadMcpVersion(selectedServer, email, {
                                  spec_content: versionUploadText.trim(),
                                  notes: versionUploadNotes.trim() || undefined,
                                  activate: false,
                                });
                                setVersions(prev => prev ? [v, ...prev] : [v]);
                                setVersionUploadText("");
                                setVersionUploadNotes("");
                                setShowUploadForm(false);
                                setToast("New version uploaded — activate it when ready.");
                              } catch (e: unknown) {
                                setVersionsError((e as Error).message ?? "Upload failed");
                              } finally {
                                setVersionUploading(false);
                              }
                            }}
                            className="flex items-center gap-2 px-5 py-2.5 rounded-xl bg-primary text-primary-foreground text-sm font-semibold disabled:opacity-40 transition-opacity hover:opacity-90"
                          >
                            {versionUploading ? <RefreshCw size={13} className="animate-spin" /> : <Upload size={13} />}
                            {versionUploading ? "Uploading…" : "Upload (don't activate yet)"}
                          </button>
                          <button
                            disabled={!versionUploadText.trim() || versionUploading}
                            onClick={async () => {
                              if (!email || !selectedServer) return;
                              setVersionUploading(true);
                              try {
                                const v = await uploadMcpVersion(selectedServer, email, {
                                  spec_content: versionUploadText.trim(),
                                  notes: versionUploadNotes.trim() || undefined,
                                  activate: true,
                                });
                                setVersions(prev => prev
                                  ? [v, ...prev.map(x => ({ ...x, is_active: 0 as 0 | 1 }))]
                                  : [v]
                                );
                                setVersionUploadText("");
                                setVersionUploadNotes("");
                                setShowUploadForm(false);
                                setToast("New version uploaded and activated — your MCP is now live with the new spec.");
                              } catch (e: unknown) {
                                setVersionsError((e as Error).message ?? "Upload failed");
                              } finally {
                                setVersionUploading(false);
                              }
                            }}
                            className="flex items-center gap-2 px-5 py-2.5 rounded-xl bg-emerald-500 text-white text-sm font-semibold disabled:opacity-40 transition-opacity hover:opacity-90"
                          >
                            {versionUploading ? <RefreshCw size={13} className="animate-spin" /> : <Play size={13} />}
                            Upload &amp; activate now
                          </button>
                          <button
                            onClick={() => { setShowUploadForm(false); setVersionUploadText(""); setVersionUploadNotes(""); }}
                            className="px-4 py-2.5 rounded-xl border border-border text-sm text-muted-foreground hover:text-foreground transition-colors"
                          >
                            Cancel
                          </button>
                        </div>
                      </motion.div>
                    )}

                    {/* Error */}
                    {versionsError && (
                      <div className="flex items-center gap-2 px-4 py-3 rounded-xl bg-red-500/10 border border-red-500/20 text-red-600 dark:text-red-400 text-sm">
                        <AlertCircle size={14} /> {versionsError}
                      </div>
                    )}

                    {/* Version list */}
                    {versionsLoading ? (
                      <div className="flex items-center justify-center py-20 gap-3 text-muted-foreground">
                        <RefreshCw size={16} className="animate-spin" /> Loading versions…
                      </div>
                    ) : versions && versions.length === 0 ? (
                      <div className="flex flex-col items-center justify-center py-20 gap-3">
                        <div className="w-12 h-12 rounded-2xl bg-muted/60 flex items-center justify-center">
                          <GitBranch size={22} className="text-muted-foreground" />
                        </div>
                        <p className="font-semibold text-foreground">No versions yet</p>
                        <p className="text-sm text-muted-foreground">Upload a new spec to create your first version snapshot.</p>
                      </div>
                    ) : versions && (
                      <div className="space-y-3">
                        {versions.map((v, i) => (
                          <motion.div
                            key={v.id}
                            initial={{ opacity: 0, x: -8 }} animate={{ opacity: 1, x: 0 }}
                            transition={{ delay: i * 0.04 }}
                            className={`rounded-2xl border bg-card p-5 flex items-start gap-4 transition-colors ${
                              v.is_active ? "border-primary/30 bg-primary/5 dark:bg-primary/5" : "border-border"
                            }`}
                          >
                            {/* Version badge */}
                            <div className={`shrink-0 flex items-center justify-center w-16 h-10 rounded-xl text-sm font-bold font-mono ${
                              v.is_active
                                ? "bg-primary text-primary-foreground"
                                : "bg-muted text-muted-foreground"
                            }`}>
                              {v.version}
                            </div>

                            {/* Meta */}
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center gap-2 flex-wrap">
                                {v.is_active ? (
                                  <span className="flex items-center gap-1 px-2 py-0.5 rounded-full bg-emerald-500/15 text-emerald-600 dark:text-emerald-400 text-xs font-semibold">
                                    <CheckCircle2 size={10} /> Active
                                  </span>
                                ) : (
                                  <span className="flex items-center gap-1 px-2 py-0.5 rounded-full bg-muted text-muted-foreground text-xs font-medium">
                                    Inactive
                                  </span>
                                )}
                                <span className="text-xs text-muted-foreground font-mono">{v.spec_hash}</span>
                                <span className="text-xs text-muted-foreground">·</span>
                                <span className="text-xs text-muted-foreground">{v.tool_count} tool{v.tool_count !== 1 ? "s" : ""}</span>
                                <span className="text-xs text-muted-foreground">·</span>
                                <span className="text-xs text-muted-foreground">{formatDate(v.created_at)}</span>
                              </div>
                              {v.notes && (
                                <p className="text-xs text-muted-foreground mt-1.5 line-clamp-2">{v.notes}</p>
                              )}
                            </div>

                            {/* Actions */}
                            <div className="shrink-0 flex items-center gap-2">
                              {!v.is_active && (
                                <>
                                  {/* Activate / rollback */}
                                  <button
                                    disabled={versionActivating === v.id}
                                    onClick={async () => {
                                      if (!email) return;
                                      setVersionActivating(v.id);
                                      setVersionsError(null);
                                      try {
                                        const activated = await activateMcpVersion(v.server_id, v.id, email);
                                        setVersions(prev => prev
                                          ? prev.map(x => ({ ...x, is_active: (x.id === activated.id ? 1 : 0) as 0 | 1 }))
                                          : null
                                        );
                                        setToast(`Activated ${activated.version} — your MCP is now serving this spec.`);
                                      } catch (e: unknown) {
                                        setVersionsError((e as Error).message ?? "Activation failed");
                                      } finally {
                                        setVersionActivating(null);
                                      }
                                    }}
                                    className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-primary/10 text-primary text-xs font-semibold hover:bg-primary/20 transition-colors disabled:opacity-40"
                                    title={i === 0 ? "Activate" : "Rollback to this version"}
                                  >
                                    {versionActivating === v.id
                                      ? <RefreshCw size={11} className="animate-spin" />
                                      : <Play size={11} />
                                    }
                                    {i === 0 ? "Activate" : "Rollback"}
                                  </button>

                                  {/* Delete */}
                                  <button
                                    disabled={versionDeleting === v.id}
                                    onClick={async () => {
                                      if (!email || !confirm(`Delete version ${v.version}? This cannot be undone.`)) return;
                                      setVersionDeleting(v.id);
                                      setVersionsError(null);
                                      try {
                                        await deleteMcpVersion(v.server_id, v.id, email);
                                        setVersions(prev => prev ? prev.filter(x => x.id !== v.id) : null);
                                        setToast(`Version ${v.version} deleted.`);
                                      } catch (e: unknown) {
                                        setVersionsError((e as Error).message ?? "Delete failed");
                                      } finally {
                                        setVersionDeleting(null);
                                      }
                                    }}
                                    className="flex items-center gap-1 p-1.5 rounded-lg border border-border text-muted-foreground hover:text-red-500 hover:border-red-500/40 transition-colors disabled:opacity-40"
                                    title="Delete this version"
                                  >
                                    {versionDeleting === v.id
                                      ? <RefreshCw size={13} className="animate-spin" />
                                      : <Trash2 size={13} />
                                    }
                                  </button>
                                </>
                              )}
                              {v.is_active && (
                                <span className="flex items-center gap-1 text-xs text-muted-foreground px-2">
                                  <ChevronRight size={12} /> Live
                                </span>
                              )}
                            </div>
                          </motion.div>
                        ))}
                      </div>
                    )}
                  </>
                )}
              </motion.div>
            )}

            {/* ══════════════════════════════════════════════════
                TAB: WEBHOOKS
            ══════════════════════════════════════════════════ */}
            {tab === "webhooks" && isPro && (
              <motion.div key="webhooks" initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }} className="space-y-6">

                {/* Header */}
                <div className="flex items-center gap-3 flex-wrap">
                  <div>
                    <h2 className="font-semibold text-sm flex items-center gap-2">
                      <WebhookIcon size={14} className="text-blue-500" /> Webhooks
                    </h2>
                    <p className="text-xs text-muted-foreground mt-0.5">Get notified in real-time when your MCPs are called or encounter errors.</p>
                  </div>
                  <div className="ml-auto">
                    <button
                      onClick={() => setShowWebhookForm(v => !v)}
                      className="flex items-center gap-2 px-4 py-2 rounded-xl bg-primary text-primary-foreground text-sm font-semibold shadow hover:opacity-90 transition-opacity"
                    >
                      <Plus size={13} /> Add webhook
                    </button>
                  </div>
                </div>

                {/* Create form */}
                {showWebhookForm && (
                  <motion.div
                    initial={{ opacity: 0, y: -8 }} animate={{ opacity: 1, y: 0 }}
                    className="rounded-2xl border border-border bg-card p-6 space-y-5"
                  >
                    <h3 className="font-semibold text-sm flex items-center gap-2">
                      <WebhookIcon size={14} className="text-primary" /> New webhook endpoint
                    </h3>

                    {/* URL */}
                    <div>
                      <label className="block text-xs font-medium text-muted-foreground mb-1.5">Endpoint URL</label>
                      <input
                        type="url"
                        value={whUrl}
                        onChange={e => setWhUrl(e.target.value)}
                        placeholder="https://your-server.com/webhooks/mcp"
                        className="w-full rounded-xl border border-border bg-muted/40 px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-primary/40"
                      />
                    </div>

                    {/* Events */}
                    <div>
                      <label className="block text-xs font-medium text-muted-foreground mb-2">Events to subscribe</label>
                      <div className="space-y-2">
                        {WEBHOOK_EVENTS.map(ev => (
                          <label key={ev.value} className="flex items-start gap-3 cursor-pointer group">
                            <input
                              type="checkbox"
                              className="mt-0.5 rounded"
                              checked={whEvents.includes(ev.value)}
                              onChange={e => setWhEvents(prev =>
                                e.target.checked ? [...prev, ev.value] : prev.filter(x => x !== ev.value)
                              )}
                            />
                            <div>
                              <p className="text-sm font-medium text-foreground">{ev.label}</p>
                              <p className="text-xs text-muted-foreground">{ev.description}</p>
                            </div>
                          </label>
                        ))}
                      </div>
                    </div>

                    {/* MCP scope */}
                    <div>
                      <label className="block text-xs font-medium text-muted-foreground mb-1.5">Scope</label>
                      <select
                        value={whServerId}
                        onChange={e => setWhServerId(e.target.value)}
                        className="w-full rounded-xl border border-border bg-muted/40 px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-primary/40"
                      >
                        <option value="__all__">All my MCPs</option>
                        {(allMcps.filter(m => !m.is_expired)).map(m => (
                          <option key={m.server_id} value={m.server_id}>{m.title}</option>
                        ))}
                      </select>
                    </div>

                    <div className="flex items-center gap-3 pt-1">
                      <button
                        disabled={!whUrl.trim() || whEvents.length === 0 || whCreating}
                        onClick={async () => {
                          if (!email) return;
                          setWhCreating(true);
                          setWebhooksError(null);
                          try {
                            const wh = await createWebhook(email, {
                              url: whUrl.trim(),
                              events: whEvents,
                              server_id: whServerId === "__all__" ? null : whServerId,
                            });
                            // Capture the plaintext secret for one-time reveal,
                            // then strip it from the row stored in state so it
                            // can never be re-read from the list.
                            if (wh.secret) {
                              setWhRevealedSecret({ id: wh.id, secret: wh.secret });
                            }
                            const safeWh: Webhook = { ...wh, secret: undefined };
                            setWebhooks(prev => prev ? [safeWh, ...prev] : [safeWh]);
                            setWhUrl(""); setWhEvents(["mcp.call.completed", "mcp.error.occurred"]);
                            setWhServerId("__all__"); setShowWebhookForm(false);
                          } catch (e: unknown) {
                            setWebhooksError((e as Error).message ?? "Failed to create webhook");
                          } finally {
                            setWhCreating(false);
                          }
                        }}
                        className="flex items-center gap-2 px-5 py-2.5 rounded-xl bg-primary text-primary-foreground text-sm font-semibold disabled:opacity-40 hover:opacity-90 transition-opacity"
                      >
                        {whCreating ? <RefreshCw size={13} className="animate-spin" /> : <Plus size={13} />}
                        {whCreating ? "Creating…" : "Create webhook"}
                      </button>
                      <button
                        onClick={() => { setShowWebhookForm(false); setWhUrl(""); }}
                        className="px-4 py-2.5 rounded-xl border border-border text-sm text-muted-foreground hover:text-foreground transition-colors"
                      >
                        Cancel
                      </button>
                    </div>
                  </motion.div>
                )}

                {/* Error */}
                {webhooksError && (
                  <div className="flex items-center gap-2 px-4 py-3 rounded-xl bg-red-500/10 border border-red-500/20 text-red-600 dark:text-red-400 text-sm">
                    <AlertCircle size={14} /> {webhooksError}
                  </div>
                )}

                {/* One-time signing-secret reveal */}
                {whRevealedSecret && (
                  <motion.div
                    initial={{ opacity: 0, y: -6 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="rounded-2xl border border-amber-500/30 bg-amber-500/10 p-5 space-y-3"
                  >
                    <div className="flex items-start gap-3">
                      <Shield size={16} className="text-amber-600 dark:text-amber-400 mt-0.5 shrink-0" />
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-semibold text-foreground">
                          Copy your signing secret now
                        </p>
                        <p className="text-xs text-muted-foreground mt-0.5">
                          This is the only time the full secret is shown. Store it in your
                          server&apos;s environment variables — use it to verify the{" "}
                          <code className="px-1 py-0.5 rounded bg-muted text-foreground">X-Webhook-Signature</code>{" "}
                          header on incoming requests.
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center gap-2 px-3 py-2.5 rounded-xl bg-background border border-border">
                      <code className="flex-1 text-xs font-mono text-foreground break-all select-all">
                        {whRevealedSecret.secret}
                      </code>
                      <button
                        onClick={() => {
                          navigator.clipboard.writeText(whRevealedSecret.secret);
                          setToast("Secret copied to clipboard!");
                        }}
                        className="shrink-0 inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-foreground text-background text-xs font-semibold hover:opacity-90 transition-opacity"
                      >
                        <Copy size={12} /> Copy
                      </button>
                    </div>
                    <div className="flex items-center justify-end">
                      <button
                        onClick={() => setWhRevealedSecret(null)}
                        className="text-xs font-medium text-muted-foreground hover:text-foreground transition-colors"
                      >
                        I&apos;ve saved it — dismiss
                      </button>
                    </div>
                  </motion.div>
                )}

                {/* List */}
                {webhooksLoading ? (
                  <div className="flex items-center justify-center py-20 gap-3 text-muted-foreground">
                    <RefreshCw size={16} className="animate-spin" /> Loading webhooks…
                  </div>
                ) : webhooks && webhooks.length === 0 ? (
                  <div className="flex flex-col items-center justify-center py-20 gap-3">
                    <div className="w-12 h-12 rounded-2xl bg-muted/60 flex items-center justify-center">
                      <WebhookIcon size={22} className="text-muted-foreground" />
                    </div>
                    <p className="font-semibold text-foreground">No webhooks yet</p>
                    <p className="text-sm text-muted-foreground">Add an endpoint above to start receiving real-time events.</p>
                  </div>
                ) : webhooks && (
                  <div className="space-y-4">
                    {webhooks.map((wh, i) => (
                      <motion.div
                        key={wh.id}
                        initial={{ opacity: 0, x: -8 }} animate={{ opacity: 1, x: 0 }}
                        transition={{ delay: i * 0.04 }}
                        className="rounded-2xl border border-border bg-card overflow-hidden"
                      >
                        {/* Webhook row */}
                        <div className="flex items-start gap-4 p-5">
                          {/* Status dot */}
                          <div className={`mt-1 shrink-0 w-2 h-2 rounded-full ${wh.is_active ? "bg-emerald-500" : "bg-muted-foreground"}`} />

                          {/* Info */}
                          <div className="flex-1 min-w-0">
                            <p className="text-sm font-semibold text-foreground truncate">{wh.url}</p>
                            <div className="flex items-center gap-2 mt-1 flex-wrap">
                              {wh.events.map((ev: string) => (
                                <span key={ev} className="px-2 py-0.5 rounded-full bg-blue-500/10 text-blue-600 dark:text-blue-400 text-xs font-medium">
                                  {ev}
                                </span>
                              ))}
                              {wh.server_id && (
                                <span className="px-2 py-0.5 rounded-full bg-muted text-muted-foreground text-xs">
                                  {allMcps.find(m => m.server_id === wh.server_id)?.title ?? wh.server_id.slice(0, 8)}
                                </span>
                              )}
                              {!wh.server_id && (
                                <span className="px-2 py-0.5 rounded-full bg-muted text-muted-foreground text-xs">All MCPs</span>
                              )}
                            </div>

                            {/* Secret — masked only. Plaintext is shown once
                                on create/rotate via the reveal banner. */}
                            <div className="flex items-center gap-2 mt-2">
                              <span className="text-xs text-muted-foreground font-mono">
                                Secret: {wh.secret_masked || "••••••••••••••••"}
                              </span>
                              <button
                                disabled={whRotating === wh.id}
                                onClick={async () => {
                                  if (!email) return;
                                  if (!confirm(
                                    "Rotate signing secret? The current secret will stop working immediately and the new one will be shown only once."
                                  )) return;
                                  setWhRotating(wh.id);
                                  try {
                                    const rotated = await rotateWebhookSecret(wh.id, email);
                                    if (rotated.secret) {
                                      setWhRevealedSecret({ id: rotated.id, secret: rotated.secret });
                                    }
                                    const safe: Webhook = { ...rotated, secret: undefined };
                                    setWebhooks(prev =>
                                      prev ? prev.map(w => (w.id === safe.id ? safe : w)) : prev
                                    );
                                  } catch (e: unknown) {
                                    setWebhooksError((e as Error).message ?? "Failed to rotate secret");
                                  } finally {
                                    setWhRotating(null);
                                  }
                                }}
                                title="Generate a new signing secret"
                                className="inline-flex items-center gap-1 text-[11px] font-medium text-muted-foreground hover:text-foreground transition-colors disabled:opacity-50"
                              >
                                <RotateCcw size={11} className={whRotating === wh.id ? "animate-spin" : ""} />
                                Rotate
                              </button>
                            </div>
                          </div>

                          {/* Actions */}
                          <div className="shrink-0 flex items-center gap-2">
                            {/* Ping */}
                            <button
                              disabled={whPinging === wh.id}
                              title="Send test ping"
                              onClick={async () => {
                                if (!email) return;
                                setWhPinging(wh.id);
                                try {
                                  const d = await pingWebhook(wh.id, email);
                                  setWhDeliveries(prev => ({ ...prev, [wh.id]: [d, ...(prev[wh.id] ?? [])] }));
                                  setWhShowDeliveries(wh.id);
                                  setToast(d.status === "delivered" ? "Ping delivered ✓" : "Ping failed — check delivery log");
                                } catch {
                                  setToast("Ping request failed");
                                } finally {
                                  setWhPinging(null);
                                }
                              }}
                              className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-muted text-muted-foreground text-xs font-medium hover:text-foreground hover:bg-muted/80 transition-colors disabled:opacity-40"
                            >
                              {whPinging === wh.id ? <RefreshCw size={11} className="animate-spin" /> : <Play size={11} />}
                              Ping
                            </button>

                            {/* Deliveries toggle */}
                            <button
                              onClick={async () => {
                                if (whShowDeliveries === wh.id) { setWhShowDeliveries(null); return; }
                                if (!whDeliveries[wh.id] && email) {
                                  try {
                                    const d = await listWebhookDeliveries(wh.id, email);
                                    setWhDeliveries(prev => ({ ...prev, [wh.id]: d }));
                                  } catch { /* ignore */ }
                                }
                                setWhShowDeliveries(wh.id);
                              }}
                              className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-muted text-muted-foreground text-xs font-medium hover:text-foreground hover:bg-muted/80 transition-colors"
                            >
                              <Activity size={11} />
                              Log
                            </button>

                            {/* Delete */}
                            <button
                              disabled={whDeleting === wh.id}
                              onClick={async () => {
                                if (!email || !confirm("Delete this webhook?")) return;
                                setWhDeleting(wh.id);
                                try {
                                  await deleteWebhook(wh.id, email);
                                  setWebhooks(prev => prev ? prev.filter(x => x.id !== wh.id) : null);
                                  setToast("Webhook deleted.");
                                } catch { setToast("Delete failed"); }
                                finally { setWhDeleting(null); }
                              }}
                              className="p-1.5 rounded-lg border border-border text-muted-foreground hover:text-red-500 hover:border-red-500/40 transition-colors disabled:opacity-40"
                            >
                              {whDeleting === wh.id ? <RefreshCw size={13} className="animate-spin" /> : <Trash2 size={13} />}
                            </button>
                          </div>
                        </div>

                        {/* Delivery log panel */}
                        {whShowDeliveries === wh.id && (
                          <div className="border-t border-border bg-muted/30 p-4">
                            <p className="text-xs font-semibold text-muted-foreground mb-3">Recent deliveries</p>
                            {!whDeliveries[wh.id] || whDeliveries[wh.id].length === 0 ? (
                              <p className="text-xs text-muted-foreground">No deliveries recorded yet. Click Ping to test.</p>
                            ) : (
                              <div className="space-y-2">
                                {whDeliveries[wh.id].slice(0, 20).map(d => (
                                  <div key={d.id} className="flex items-center gap-3 text-xs">
                                    {d.status === "delivered"
                                      ? <CheckCircle2 size={12} className="text-emerald-500 shrink-0" />
                                      : <XCircle size={12} className="text-red-500 shrink-0" />
                                    }
                                    <span className="font-mono text-muted-foreground w-28 shrink-0">{d.event_type}</span>
                                    <span className={`px-1.5 py-0.5 rounded font-mono ${d.status === "delivered" ? "bg-emerald-500/10 text-emerald-600 dark:text-emerald-400" : "bg-red-500/10 text-red-600 dark:text-red-400"}`}>
                                      {d.response_status ?? "—"}
                                    </span>
                                    <span className="text-muted-foreground">{d.attempts} attempt{d.attempts !== 1 ? "s" : ""}</span>
                                    {d.error_message && <span className="text-red-500 truncate max-w-xs">{d.error_message}</span>}
                                    <span className="ml-auto text-muted-foreground shrink-0">{formatRelative(d.delivered_at)}</span>
                                  </div>
                                ))}
                              </div>
                            )}
                          </div>
                        )}
                      </motion.div>
                    ))}
                  </div>
                )}

                {/* Signing docs callout */}
                {webhooks && webhooks.length > 0 && (
                  <div className="rounded-xl border border-border bg-muted/30 p-4 text-xs text-muted-foreground">
                    <p className="font-semibold text-foreground mb-1">Verifying webhook signatures</p>
                    <p>Every delivery includes an <code className="font-mono bg-muted px-1 py-0.5 rounded">X-Webhook-Signature</code> header with an HMAC-SHA256 signature. Verify it using your webhook secret to ensure the payload hasn't been tampered with.</p>
                    <pre className="mt-2 bg-muted rounded-lg p-3 overflow-x-auto text-[11px] leading-relaxed">{`import hmac, hashlib

def verify(secret: str, body: bytes, sig_header: str) -> bool:
    expected = "sha256=" + hmac.new(
        secret.encode(), body, hashlib.sha256
    ).hexdigest()
    return hmac.compare_digest(expected, sig_header)`}</pre>
                  </div>
                )}

              </motion.div>
            )}

            {/* ══════════════════════════════════════════════════
                TAB: SECURITY
            ══════════════════════════════════════════════════ */}
            {tab === "security" && isPro && (
              <motion.div key="security" initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }} className="space-y-8">

                {/* ── API Keys ─────────────────────────────── */}
                <div className="rounded-xl border border-border bg-card">
                  <div className="flex items-center justify-between px-5 py-4 border-b border-border">
                    <div className="flex items-center gap-2">
                      <Key size={16} className="text-primary" />
                      <h3 className="font-semibold text-foreground">API Keys</h3>
                    </div>
                    <button
                      className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-primary text-primary-foreground text-xs font-semibold hover:opacity-90 transition-opacity"
                      onClick={async () => {
                        if (!secNewKeyName.trim()) return;
                        setSecCreatingKey(true);
                        try {
                          const key = await createApiKey(secNewKeyName.trim());
                          setSecApiKeys(prev => [key, ...(prev ?? [])]);
                          setSecRevealedKey(key.raw_key ?? null);
                          setSecNewKeyName("Default");
                          setToast("API key created — save it now, it won't be shown again.");
                        } catch (e: unknown) {
                          setToast(e instanceof Error ? e.message : "Failed to create key");
                        } finally {
                          setSecCreatingKey(false);
                        }
                      }}
                      disabled={secCreatingKey}
                    >
                      {secCreatingKey ? <RefreshCw size={12} className="animate-spin" /> : <Plus size={12} />}
                      New Key
                    </button>
                  </div>

                  {/* New key name input */}
                  <div className="px-5 py-3 border-b border-border/50 flex items-center gap-3">
                    <input
                      value={secNewKeyName}
                      onChange={e => setSecNewKeyName(e.target.value)}
                      placeholder="Key name (e.g. Production)"
                      className="flex-1 text-sm bg-muted/50 border border-border rounded-lg px-3 py-1.5 outline-none focus:ring-1 focus:ring-primary/40"
                    />
                  </div>

                  {/* Revealed key banner */}
                  {secRevealedKey && (
                    <div className="mx-5 my-3 flex items-center gap-3 px-4 py-3 rounded-xl bg-amber-500/10 border border-amber-500/30">
                      <AlertTriangle size={14} className="text-amber-500 shrink-0" />
                      <p className="text-xs text-amber-600 dark:text-amber-400 font-medium">Copy now — shown only once</p>
                      <code className="flex-1 font-mono text-xs text-foreground bg-muted px-2 py-1 rounded truncate">{secRevealedKey}</code>
                      <button onClick={() => { copyText(secRevealedKey); setToast("Copied!"); }} className="p-1.5 rounded hover:bg-muted"><Copy size={12} /></button>
                      <button onClick={() => setSecRevealedKey(null)} className="p-1.5 rounded hover:bg-muted text-muted-foreground hover:text-foreground"><XCircle size={12} /></button>
                    </div>
                  )}

                  {/* Keys list */}
                  {secApiKeysLoading ? (
                    <div className="flex items-center justify-center py-12 gap-2 text-muted-foreground text-sm">
                      <RefreshCw size={14} className="animate-spin" /> Loading…
                    </div>
                  ) : !secApiKeys || secApiKeys.length === 0 ? (
                    <p className="px-5 py-10 text-center text-sm text-muted-foreground">No API keys yet. Create one above to authenticate programmatic requests.</p>
                  ) : (
                    <div className="divide-y divide-border/50">
                      {secApiKeys.map(k => (
                        <div key={k.id} className="flex items-center gap-4 px-5 py-3">
                          <div className="flex-1 min-w-0">
                            <p className="text-sm font-medium text-foreground">{k.name}</p>
                            <p className="text-xs text-muted-foreground font-mono">{k.key_prefix}…  · created {formatDate(k.created_at)}{k.last_used_at ? ` · last used ${formatRelative(k.last_used_at)}` : " · never used"}</p>
                          </div>
                          {k.is_active ? (
                            <span className="px-2 py-0.5 rounded-full bg-emerald-500/10 text-emerald-600 dark:text-emerald-400 text-[11px] font-medium">active</span>
                          ) : (
                            <span className="px-2 py-0.5 rounded-full bg-red-500/10 text-red-500 text-[11px] font-medium">revoked</span>
                          )}
                          <div className="flex items-center gap-1.5">
                            {k.is_active === 1 && (
                              <>
                                <button
                                  title="Rotate key"
                                  onClick={async () => {
                                    if (!confirm("Rotate this key? The current key will be revoked immediately.")) return;
                                    try {
                                      const newKey = await rotateApiKey(k.id);
                                      setSecApiKeys(prev => prev ? [newKey, ...prev.filter(x => x.id !== k.id)] : [newKey]);
                                      setSecRevealedKey(newKey.raw_key ?? null);
                                      setToast("Key rotated — save the new one now.");
                                    } catch (e: unknown) {
                                      setToast(e instanceof Error ? e.message : "Rotation failed");
                                    }
                                  }}
                                  className="p-1.5 rounded-lg border border-border text-muted-foreground hover:text-primary hover:border-primary/40 transition-colors"
                                >
                                  <RotateCcw size={13} />
                                </button>
                                <button
                                  title="Revoke key"
                                  onClick={async () => {
                                    if (!confirm("Revoke this key? This cannot be undone.")) return;
                                    try {
                                      await revokeApiKey(k.id);
                                      setSecApiKeys(prev => prev ? prev.map(x => x.id === k.id ? { ...x, is_active: 0 as const } : x) : null);
                                      setToast("Key revoked.");
                                    } catch { setToast("Revoke failed"); }
                                  }}
                                  className="p-1.5 rounded-lg border border-border text-muted-foreground hover:text-red-500 hover:border-red-500/40 transition-colors"
                                >
                                  <Trash2 size={13} />
                                </button>
                              </>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}

                  <div className="px-5 py-3 border-t border-border/50 bg-muted/20 rounded-b-xl">
                    <p className="text-xs text-muted-foreground">Pass your API key in the <code className="font-mono bg-muted px-1 py-0.5 rounded">Authorization: Bearer &lt;key&gt;</code> header for programmatic access.</p>
                  </div>
                </div>

                {/* ── 2FA ─────────────────────────────────── */}
                <div className="rounded-xl border border-border bg-card">
                  <div className="flex items-center gap-2 px-5 py-4 border-b border-border">
                    <Smartphone size={16} className="text-primary" />
                    <h3 className="font-semibold text-foreground">Two-Factor Authentication</h3>
                    {sec2fa?.enabled && (
                      <span className="ml-auto px-2 py-0.5 rounded-full bg-emerald-500/10 text-emerald-600 dark:text-emerald-400 text-[11px] font-medium">Enabled</span>
                    )}
                  </div>

                  <div className="px-5 py-5 space-y-4">
                    {!sec2fa ? (
                      <div className="flex items-center gap-2 text-sm text-muted-foreground"><RefreshCw size={13} className="animate-spin" /> Loading…</div>
                    ) : sec2fa.enabled ? (
                      <div className="space-y-3">
                        <p className="text-sm text-muted-foreground">2FA is active on your account. Use your authenticator app when logging in.</p>
                        {sec2faSection === "disable" ? (
                          <div className="flex items-center gap-2">
                            <input
                              value={sec2faCode}
                              onChange={e => setSec2faCode(e.target.value)}
                              placeholder="Enter 6-digit code"
                              className="w-44 text-sm bg-muted/50 border border-border rounded-lg px-3 py-1.5 font-mono outline-none focus:ring-1 focus:ring-primary/40"
                              maxLength={6}
                            />
                            <button
                              disabled={sec2faLoading || sec2faCode.length < 6}
                              onClick={async () => {
                                setSec2faLoading(true);
                                try {
                                  await disable2fa(sec2faCode);
                                  setSec2fa({ enabled: false, configured: true });
                                  setSec2faSection("idle");
                                  setSec2faCode("");
                                  setToast("2FA disabled.");
                                } catch (e: unknown) {
                                  setToast(e instanceof Error ? e.message : "Invalid code");
                                } finally { setSec2faLoading(false); }
                              }}
                              className="px-3 py-1.5 rounded-lg bg-red-500 text-white text-sm font-medium hover:opacity-90 disabled:opacity-40"
                            >
                              {sec2faLoading ? <RefreshCw size={13} className="animate-spin" /> : "Confirm disable"}
                            </button>
                            <button onClick={() => { setSec2faSection("idle"); setSec2faCode(""); }} className="text-sm text-muted-foreground hover:text-foreground">Cancel</button>
                          </div>
                        ) : (
                          <button onClick={() => setSec2faSection("disable")} className="px-3 py-1.5 rounded-lg border border-red-500/40 text-red-500 text-sm font-medium hover:bg-red-500/5 transition-colors">
                            Disable 2FA
                          </button>
                        )}
                      </div>
                    ) : sec2faSection === "setup" && sec2faSetup ? (
                      <div className="space-y-4">
                        <p className="text-sm text-muted-foreground">Scan this QR code with your authenticator app (Google Authenticator, Authy, etc.), then enter the 6-digit code to confirm.</p>
                        <div className="flex flex-col items-start gap-3">
                          <div className="p-4 bg-white rounded-xl inline-block border border-border">
                            {/* QR rendered as text URI — user can use a QR lib or just show the URI */}
                            <code className="text-[10px] font-mono text-gray-700 break-all max-w-xs block">{sec2faSetup.uri}</code>
                          </div>
                          <p className="text-xs text-muted-foreground">Or enter this secret manually: <code className="font-mono bg-muted px-1 py-0.5 rounded">{sec2faSetup.secret}</code></p>
                          <div className="flex items-center gap-2">
                            <input
                              value={sec2faCode}
                              onChange={e => setSec2faCode(e.target.value)}
                              placeholder="6-digit code"
                              className="w-36 text-sm bg-muted/50 border border-border rounded-lg px-3 py-1.5 font-mono outline-none focus:ring-1 focus:ring-primary/40"
                              maxLength={6}
                            />
                            <button
                              disabled={sec2faLoading || sec2faCode.length < 6}
                              onClick={async () => {
                                setSec2faLoading(true);
                                try {
                                  await verify2fa(sec2faCode);
                                  setSec2fa({ enabled: true, configured: true });
                                  setSec2faSection("idle");
                                  setSec2faCode("");
                                  setSec2faSetup(null);
                                  setToast("2FA enabled! Your account is now protected.");
                                } catch (e: unknown) {
                                  setToast(e instanceof Error ? e.message : "Invalid code");
                                } finally { setSec2faLoading(false); }
                              }}
                              className="px-3 py-1.5 rounded-lg bg-primary text-primary-foreground text-sm font-medium hover:opacity-90 disabled:opacity-40"
                            >
                              {sec2faLoading ? <RefreshCw size={13} className="animate-spin" /> : "Verify & Enable"}
                            </button>
                            <button onClick={() => { setSec2faSection("idle"); setSec2faSetup(null); setSec2faCode(""); }} className="text-sm text-muted-foreground hover:text-foreground">Cancel</button>
                          </div>
                        </div>
                      </div>
                    ) : (
                      <div className="space-y-3">
                        <p className="text-sm text-muted-foreground">Add an extra layer of security with a time-based one-time password (TOTP) app.</p>
                        <button
                          disabled={sec2faLoading}
                          onClick={async () => {
                            setSec2faLoading(true);
                            try {
                              const s = await setup2fa();
                              setSec2faSetup(s);
                              setSec2faSection("setup");
                            } catch (e: unknown) {
                              setToast(e instanceof Error ? e.message : "Failed to set up 2FA");
                            } finally { setSec2faLoading(false); }
                          }}
                          className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-primary text-primary-foreground text-sm font-medium hover:opacity-90 disabled:opacity-40"
                        >
                          {sec2faLoading ? <RefreshCw size={13} className="animate-spin" /> : <Smartphone size={13} />}
                          Set up 2FA
                        </button>
                      </div>
                    )}
                  </div>
                </div>

                {/* ── IP Allowlist ─────────────────────────── */}
                <div className="rounded-xl border border-border bg-card">
                  <div className="flex items-center gap-2 px-5 py-4 border-b border-border">
                    <MapPin size={16} className="text-primary" />
                    <h3 className="font-semibold text-foreground">IP Allowlist</h3>
                    <span className="ml-auto px-2 py-0.5 rounded-full bg-violet-500/10 text-violet-600 dark:text-violet-400 text-[11px] font-medium">Pro</span>
                  </div>
                  <div className="px-5 py-4 space-y-4">
                    <p className="text-sm text-muted-foreground">Restrict MCP access to specific IP addresses or CIDR ranges. Leave empty to allow all IPs.</p>

                    {/* Add entry */}
                    <div className="flex items-center gap-2">
                      <input
                        value={secNewCidr}
                        onChange={e => setSecNewCidr(e.target.value)}
                        placeholder="IP or CIDR  e.g. 203.0.113.0/24"
                        className="flex-1 text-sm bg-muted/50 border border-border rounded-lg px-3 py-1.5 font-mono outline-none focus:ring-1 focus:ring-primary/40"
                      />
                      <input
                        value={secNewCidrLabel}
                        onChange={e => setSecNewCidrLabel(e.target.value)}
                        placeholder="Label (optional)"
                        className="w-36 text-sm bg-muted/50 border border-border rounded-lg px-3 py-1.5 outline-none focus:ring-1 focus:ring-primary/40"
                      />
                      <button
                        disabled={secAddingIp || !secNewCidr.trim()}
                        onClick={async () => {
                          setSecAddingIp(true);
                          try {
                            const entry = await addIpAllowlistEntry(secNewCidr.trim(), secNewCidrLabel.trim() || undefined);
                            setSecIpList(prev => [entry, ...(prev ?? [])]);
                            setSecNewCidr("");
                            setSecNewCidrLabel("");
                            setToast("IP entry added.");
                          } catch (e: unknown) {
                            setToast(e instanceof Error ? e.message : "Failed to add entry");
                          } finally { setSecAddingIp(false); }
                        }}
                        className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-primary text-primary-foreground text-xs font-semibold hover:opacity-90 disabled:opacity-40"
                      >
                        {secAddingIp ? <RefreshCw size={12} className="animate-spin" /> : <Plus size={12} />}
                        Add
                      </button>
                    </div>

                    {/* Entries list */}
                    {secIpLoading ? (
                      <div className="text-sm text-muted-foreground flex items-center gap-2"><RefreshCw size={13} className="animate-spin" /> Loading…</div>
                    ) : !secIpList || secIpList.length === 0 ? (
                      <p className="text-sm text-muted-foreground">No entries — all IPs are currently allowed.</p>
                    ) : (
                      <div className="rounded-xl border border-border overflow-hidden">
                        {secIpList.map((entry, idx) => (
                          <div key={entry.id} className={`flex items-center gap-4 px-4 py-2.5 ${idx < secIpList.length - 1 ? "border-b border-border/50" : ""}`}>
                            <code className="text-sm font-mono text-foreground">{entry.cidr}</code>
                            {entry.label && <span className="text-xs text-muted-foreground">{entry.label}</span>}
                            {entry.server_id && <span className="text-xs text-muted-foreground font-mono">/{entry.server_id}</span>}
                            <span className="ml-auto text-xs text-muted-foreground">{formatDate(entry.created_at)}</span>
                            <button
                              onClick={async () => {
                                try {
                                  await deleteIpAllowlistEntry(entry.id);
                                  setSecIpList(prev => prev ? prev.filter(x => x.id !== entry.id) : null);
                                  setToast("Entry removed.");
                                } catch { setToast("Failed to remove entry"); }
                              }}
                              className="p-1.5 rounded-lg border border-border text-muted-foreground hover:text-red-500 hover:border-red-500/40 transition-colors"
                            >
                              <Trash2 size={12} />
                            </button>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>

                {/* ── Audit Log ────────────────────────────── */}
                <div className="rounded-xl border border-border bg-card">
                  <div className="flex items-center justify-between px-5 py-4 border-b border-border">
                    <div className="flex items-center gap-2">
                      <List size={16} className="text-primary" />
                      <h3 className="font-semibold text-foreground">Audit Log</h3>
                    </div>
                    <select
                      value={secAuditDays}
                      onChange={e => setSecAuditDays(Number(e.target.value))}
                      className="text-xs bg-muted/50 border border-border rounded-lg px-2 py-1 outline-none"
                    >
                      <option value={7}>Last 7 days</option>
                      <option value={30}>Last 30 days</option>
                      <option value={90}>Last 90 days</option>
                    </select>
                  </div>

                  {secAuditLoading ? (
                    <div className="flex items-center justify-center py-12 gap-2 text-sm text-muted-foreground">
                      <RefreshCw size={14} className="animate-spin" /> Loading…
                    </div>
                  ) : !secAuditLogs || secAuditLogs.length === 0 ? (
                    <p className="px-5 py-10 text-center text-sm text-muted-foreground">No audit events in the last {secAuditDays} days.</p>
                  ) : (
                    <div className="divide-y divide-border/50 max-h-[480px] overflow-y-auto">
                      {secAuditLogs.map(ev => {
                        const ts = new Date(ev.created_at).toLocaleString(undefined, { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" });
                        const isDestructive = ev.action.includes("deleted") || ev.action.includes("revoked") || ev.action.includes("disabled");
                        const isPositive = ev.action.includes("created") || ev.action.includes("enabled") || ev.action.includes("upgraded");
                        return (
                          <div key={ev.id} className="flex items-start gap-4 px-5 py-3">
                            <div className={`w-2 h-2 rounded-full mt-1.5 shrink-0 ${isDestructive ? "bg-red-500" : isPositive ? "bg-emerald-500" : "bg-muted-foreground/40"}`} />
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center gap-2 flex-wrap">
                                <code className="text-xs font-mono text-foreground">{ev.action}</code>
                                {ev.resource_id && <span className="text-xs text-muted-foreground font-mono truncate max-w-[200px]">{ev.resource_id}</span>}
                              </div>
                              {ev.details && Object.keys(ev.details).length > 0 && (
                                <p className="text-xs text-muted-foreground mt-0.5 truncate">
                                  {Object.entries(ev.details).map(([k, v]) => `${k}: ${String(v)}`).join(" · ")}
                                </p>
                              )}
                              {ev.ip_address && <p className="text-xs text-muted-foreground/60">{ev.ip_address}</p>}
                            </div>
                            <span className="text-xs text-muted-foreground shrink-0">{ts}</span>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>

              </motion.div>
            )}

            {/* ══════════════════════════════════════════════════
                TAB: INTEGRATIONS
            ══════════════════════════════════════════════════ */}
            {tab === "integrations" && isPro && (
              <motion.div key="integrations" initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }} className="space-y-6">

                <div className="rounded-xl border border-border bg-card">
                  <div className="flex items-center justify-between px-5 py-4 border-b border-border">
                    <div className="flex items-center gap-2">
                      <Link2 size={16} className="text-primary" />
                      <h3 className="font-semibold text-foreground">Registry Integrations</h3>
                    </div>
                    <a
                      href="/registry"
                      className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-primary text-primary-foreground text-xs font-semibold hover:opacity-90 transition-opacity"
                    >
                      <Plus size={12} /> Browse Registry
                    </a>
                  </div>

                  <div className="p-5">
                    {integrationsLoading ? (
                      <div className="flex items-center gap-2 text-sm text-muted-foreground py-4">
                        <RefreshCw size={14} className="animate-spin" /> Loading integrations…
                      </div>
                    ) : integrationsError ? (
                      <p className="text-sm text-red-500">{integrationsError}</p>
                    ) : !integrations || integrations.length === 0 ? (
                      <div className="text-center py-10 text-muted-foreground">
                        <Link2 size={32} className="mx-auto mb-3 opacity-25" />
                        <p className="font-medium text-sm">No connected integrations yet</p>
                        <p className="text-xs mt-1 mb-4">Connect Notion, Linear, and other authenticated MCPs from the Registry.</p>
                        <a
                          href="/registry"
                          className="inline-flex items-center gap-1.5 px-4 py-2 rounded-xl bg-primary text-primary-foreground text-sm font-semibold hover:opacity-90 transition-opacity"
                        >
                          <Plus size={14} /> Browse Registry
                        </a>
                      </div>
                    ) : (
                      <div className="divide-y divide-border/50">
                        {integrations.map((conn) => (
                          <div key={conn.connectionId} className="flex items-center gap-4 py-4 first:pt-0 last:pb-0">
                            {/* Icon + name */}
                            <div className="w-10 h-10 rounded-xl bg-muted/60 flex items-center justify-center text-2xl flex-shrink-0">
                              {conn.icon || "🔌"}
                            </div>
                            <div className="flex-1 min-w-0">
                              <p className="font-semibold text-sm text-foreground">{conn.name}</p>
                              <p className="text-xs text-muted-foreground">
                                Connected {formatDate(conn.connectedAt)}
                                {conn.lastUsedAt ? ` · Last used ${formatRelative(conn.lastUsedAt)}` : ""}
                              </p>
                              {/* Personal MCP URL */}
                              <div className="flex items-center gap-2 mt-2 bg-muted/40 border border-border/60 rounded-lg px-3 py-1.5 max-w-xl">
                                <code className="flex-1 text-[11px] font-mono text-foreground truncate">{conn.mcpUrl}</code>
                                <button
                                  type="button"
                                  onClick={() => void navigator.clipboard.writeText(conn.mcpUrl).then(() => setToast("URL copied!"))}
                                  className="flex-shrink-0 p-1 rounded text-muted-foreground hover:text-foreground transition-colors"
                                  aria-label="Copy MCP URL"
                                >
                                  <Copy size={11} />
                                </button>
                              </div>
                            </div>
                            {/* Revoke button */}
                            <button
                              type="button"
                              disabled={revokingConnection === conn.connectionId}
                              onClick={async () => {
                                setRevokingConnection(conn.connectionId);
                                try {
                                  const { revokeRegistryConnection } = await import("../lib/registryApi");
                                  await revokeRegistryConnection(conn.connectionId, { email: email! });
                                  setIntegrations(prev => prev ? prev.filter(c => c.connectionId !== conn.connectionId) : null);
                                  setToast("Integration revoked.");
                                } catch (e) {
                                  setToast(e instanceof Error ? e.message : "Failed to revoke");
                                } finally {
                                  setRevokingConnection(null);
                                }
                              }}
                              className="flex-shrink-0 p-2 rounded-lg border border-border text-muted-foreground hover:text-red-500 hover:border-red-500/40 transition-colors disabled:opacity-40"
                              title="Revoke connection"
                            >
                              {revokingConnection === conn.connectionId
                                ? <RefreshCw size={14} className="animate-spin" />
                                : <Trash2 size={14} />
                              }
                            </button>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>

                {/* Help note */}
                <div className="flex items-start gap-3 text-xs text-muted-foreground bg-muted/40 rounded-xl px-4 py-3 border border-border/60">
                  <AlertCircle size={14} className="flex-shrink-0 mt-0.5 text-primary/60" />
                  <span>
                    Each integration gives you a personal MCP URL with your credentials baked in.
                    Revoking an integration immediately invalidates the URL — any MCP client using it will get a 404.
                    {!isPro && " Free plan supports up to 3 integrations."}
                  </span>
                </div>

              </motion.div>
            )}

          </AnimatePresence>
        )}
      </main>

      <Footer />
    </div>
  );
}
